<template>
  <span v-resize="checkMobile">
    <loading :active.sync="isLoading" :is-full-page="true" color="#1976d2"
      :background-color="$vuetify.theme.dark ? '#000' : ''"></loading>
    <v-card flat color="transparent" class="tx-page pb-4" data-testid="transactions-page">
      <!-- FT-073: для MP-клиента тулбар (баланс/статус) скрыт — всё в карточке подписки ниже. -->
      <!-- «Кабинет счёта»: баланс — отдельная карточка в едином языке с карточкой тарифа
           (accent-полоса + консистентные отступы), а не app-bar. -->
      <v-card v-if="!isMpClient" flat class="tx-card mt-3 mx-3">
        <v-card-text class="pa-5">
        <!-- Для Invoice клиентов - другой интерфейс -->
        <v-row v-if="isInvoiceClient" align="center" class="text-center">
          <v-col cols="12" lg="4" md="4" sm="12" class="d-flex justify-center align-center">
            <h2 class="text-center">{{ $t("other.balance") }}:
              <span class="font-weight-bold" :class="main_company && main_company.balance < 0 ? 'red--text' : ''">
                {{ main_company ? parseFloat(main_company.balance).toFixed(2) : 0 }} 
                {{ main_company && main_company.plan && main_company.plan.currency ? main_company.plan.currency : (main_company && main_company.currency) }}
              </span>
            </h2>
          </v-col>
          <v-col cols="12" lg="4" md="4" sm="12" class="d-flex justify-center align-center">
            <div class="text-center">
              <v-icon small class="mr-1">mdi-calendar-clock</v-icon>
              <span>{{ $t("tariffs.next_invoice_date") }}: <strong>{{ nextPaymentDate || '-' }}</strong></span>
            </div>
          </v-col>
          <v-col cols="12" lg="4" md="4" sm="12" class="d-flex justify-center align-center">
            <v-chip v-if="main_company && main_company.balance < 0" color="warning" outlined>
              <v-icon left small>mdi-file-document-outline</v-icon>
              {{ $t("tariffs.invoice_will_be_issued") }}
            </v-chip>
            <v-chip v-else color="success" outlined>
              <v-icon left small>mdi-check</v-icon>
              {{ $t("tariffs.balance_ok") }}
            </v-chip>
          </v-col>
        </v-row>
        <!-- Для обычных клиентов - слева баланс + чип здоровья, справа действия -->
        <div v-else class="d-flex align-center flex-wrap" style="gap:20px">
          <div class="d-flex align-center" style="gap:14px;flex:1 1 260px">
            <div class="tx-icon-box"><v-icon color="primary">mdi-wallet-outline</v-icon></div>
            <div>
              <div class="text-overline grey--text mb-n1">{{ $t("other.balance") }}</div>
              <div class="d-flex align-center flex-wrap" style="gap:10px">
                <span class="text-h4 font-weight-medium">{{ main_company ? parseFloat(main_company.balance).toFixed(2) : 0 }} {{ planCurrency }}</span>
                <v-chip small outlined :color="balanceHealthy ? 'success' : 'error'">
                  <v-icon left x-small>{{ balanceHealthy ? 'mdi-check' : 'mdi-alert-circle-outline' }}</v-icon>
                  {{ balanceHealthy ? $t('tariffs.balance_ok') : $t('tariffs.balance_low') }}
                </v-chip>
              </div>
            </div>
          </div>
          <div class="d-flex align-center flex-wrap" style="gap:10px">
            <v-btn v-if="!isNative" color="success" @click="transactionStart()">
              {{ $t("other.add_balance") }}
              <v-icon class="ml-1"> mdi-plus </v-icon>
            </v-btn>
            <v-btn v-if="isIos" color="success" @click="startInAppPurchaseIos()"
              :disabled="(main_company && main_company.plan && main_company.plan.currency == 'RUB') || (main_company && main_company.currency == 'RUB')">
              {{ $t("other.add_balance") }}
              <v-icon class="ml-1"> mdi-plus </v-icon>
            </v-btn>
            <v-btn v-if="isAndroid" color="success" @click="startInAppPurchaseAndroid()"
              :disabled="(main_company && main_company.plan && main_company.plan.currency == 'RUB') || (main_company && main_company.currency == 'RUB')">
              {{ $t("other.add_balance") }}
              <v-icon class="ml-1"> mdi-plus </v-icon>
            </v-btn>
            <v-btn color="info" @click="openRebillDialog()" v-if="
              isTinkoffPayment &&
              $store.getters.getMainCompany.rebill_id &&
              $store.getters.getMainCompany.card_id
            ">
              {{ $t("transfers.edit_rebill") }}
              <v-icon class="ml-1"> mdi-reload </v-icon>
            </v-btn>
            <v-btn v-else-if="
              isTinkoffPayment &&
              $store.getters.getMainCompany &&
              $store.getters.getMainCompany.currency == 'RUB'
            " color="info" @click="openRebillDialog()">
              {{ $t("transfers.start_recurrent") }}
              <v-icon class="ml-1"> mdi-credit-card-plus-outline </v-icon>
            </v-btn>
            <v-btn color="error" icon @click="removeRecurrent()" class="mx-2" v-if="
              isTinkoffPayment &&
              $store.getters.getMainCompany.rebill_id &&
              $store.getters.getMainCompany.card_id
            ">
              <v-icon class="ml-1"> mdi-credit-card-off-outline </v-icon>
            </v-btn>
          </div>
        </div>
        </v-card-text>
      </v-card>
      <!-- FT-073: маркетинг-карточка подписки MP (заменяет балансовый plan-блок). Статус + тариф +
           месячная цена со сметой + что входит + метрики использования + объяснение списания + CTA. -->
      <v-card v-if="isMpClient && main_company && main_company.plan" outlined class="mt-4 mx-3 overflow-hidden">
        <div :style="`height:4px;background:var(--v-${mpStatusColor}-base, currentColor)`"></div>
        <v-card-text class="pa-5">
          <div class="d-flex align-center mb-4">
            <v-icon left large :color="mpStatusColor">mdi-autorenew</v-icon>
            <span class="text-h6 font-weight-medium">{{ $t("stripe_mp_lifecycle.subscription") }}</span>
            <v-chip class="ml-3" small :color="mpStatusColor" outlined>{{ mpStatusText }}</v-chip>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <div class="text-overline grey--text mb-n1">{{ $t("plans.current_plan") }}</div>
              <div class="text-h5 font-weight-medium mb-3">{{ main_company.plan.name }}</div>
              <div class="d-flex align-baseline flex-wrap">
                <span class="text-h3 font-weight-medium">{{ mpUnitPriceMonthly }}&nbsp;{{ main_company.plan.currency }}</span>
                <span class="text-body-2 grey--text ml-2">{{ $t("stripe_mp_lifecycle.per_unit_month") }}</span>
              </div>
              <div class="text-subtitle-1 info--text mt-2">
                <v-icon small color="info" class="mb-1">mdi-calculator</v-icon>
                {{ $t("stripe_mp_lifecycle.monthly_estimate", { sum: mpMonthlyEstimate, currency: main_company.plan.currency, units: main_company.active_cars_count }) }}
              </div>
              <v-divider class="my-4"></v-divider>
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="grey--text">{{ $t("stripe_mp_lifecycle.min_label") }}</span>
                <span>{{ mpMinMonthly }} {{ main_company.plan.currency }}</span>
              </div>
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="grey--text">{{ $t("stripe_mp_lifecycle.billing_period") }}</span>
                <span>{{ $t("stripe_mp_lifecycle.billing_period_monthly") }}</span>
              </div>
              <div v-if="mpNextCharge" class="d-flex justify-space-between align-center">
                <span class="grey--text"><v-icon x-small color="info" class="mb-1 mr-1">mdi-calendar-clock</v-icon>{{ $t("stripe_mp_lifecycle.next_charge") }}</span>
                <span class="font-weight-medium info--text">{{ mpNextCharge }}</span>
              </div>
            </v-col>
          </v-row>
          <v-row class="mt-1">
            <v-col v-for="m in mpUsage" :key="m.label" cols="12" sm="4" class="py-2">
              <div class="mp-metric pa-3">
                <div class="text-body-2 grey--text d-flex align-center">
                  {{ m.label }}
                  <v-icon v-if="m.over" x-small color="error" class="ml-1">mdi-alert</v-icon>
                </div>
                <div class="text-h5 font-weight-medium mt-1" :class="m.over ? 'error--text' : ''">
                  {{ m.value }} <span class="text-body-2 grey--text">/ {{ m.limit }}</span>
                </div>
                <v-progress-linear :value="m.pct" :color="m.over ? 'error' : m.color" height="6" rounded class="mt-2"></v-progress-linear>
              </div>
            </v-col>
          </v-row>
          <v-alert dense text type="info" class="mt-4 mb-0 text-body-2">{{ $t("stripe_mp_lifecycle.billing_explainer") }}</v-alert>
          <p v-if="isMpPreSubscription" class="mt-4 mb-0 text-body-2">{{ $t("stripe_mp_lifecycle.trial_hint") }}</p>
          <p v-else-if="main_company.stripe_mp_status === 'past_due'" class="mt-4 mb-0 warning--text text-body-2">{{ $t("stripe_mp_lifecycle.past_due_hint") }}</p>
          <div class="d-flex flex-wrap mt-4" style="gap:10px">
            <v-btn v-if="mpNeedsSubscribe && !isNative" color="success" large style="flex:1 1 200px" @click="mpCheckout" :loading="isLoadingMpCheckout">
              <v-icon left>mdi-credit-card-plus-outline</v-icon>{{ $t("stripe_mp_lifecycle.connect_subscription") }}
            </v-btn>
            <v-btn color="primary" large style="flex:1 1 200px" @click="showChangePlanDialog = true">
              <v-icon left>mdi-swap-horizontal</v-icon>{{ $t("plans.change_plan") }}
            </v-btn>
            <v-btn v-if="canCancelMp && !isNative" color="info" outlined large style="flex:1 1 200px" @click="mpUpdateCard" :loading="isLoadingMpUpdateCard">
              <v-icon left>mdi-credit-card-outline</v-icon>{{ $t("stripe_mp_lifecycle.change_card") }}
            </v-btn>
            <v-btn v-if="canCancelMp" color="error" outlined large style="flex:1 1 200px" @click="mpCancel" :loading="isLoadingMpCancel">
              {{ $t("stripe_mp_lifecycle.cancel_btn") }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
      <v-row v-if="main_company && main_company.plan && main_company.plan.name && !isMpClient" class="mt-4 mx-0">
        <v-col cols="12" v-if="isInvoiceClient">
          <v-card flat>
            <v-card-text>
              <v-row>
                <!-- Биллинг адрес (для invoice клиентов) -->
                <v-col cols="12">
                  <h3 class="mb-4">
                    <v-icon left color="primary">mdi-file-document-outline</v-icon>
                    {{ $t("tariffs.billing_address") }}
                    <span class="red--text">*</span>
                  </h3>
                  <p class="caption grey--text">{{ $t("tariffs.billing_address_info") }}</p>
                </v-col>
                <v-col cols="12" lg="3" md="6" sm="12" xs="12">
                  <v-autocomplete
                    v-model="billingAddress.country_code"
                    :items="sortedCountries"
                    item-text="text"
                    item-value="value"
                    :label="$t('custom.country') + ' *'"
                    outlined
                    :rules="[v => !!v || $t('errors.required_field')]"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" lg="3" md="6" sm="12" xs="12">
                  <v-text-field
                    v-model="billingAddress.city"
                    :label="$t('custom.city') + ' *'"
                    outlined
                    :rules="[v => !!v || $t('errors.required_field')]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" lg="3" md="6" sm="12" xs="12">
                  <v-text-field
                    v-model="billingAddress.first_line"
                    :label="$t('custom.address') + ' *'"
                    outlined
                    :rules="[v => !!v || $t('errors.required_field')]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" lg="3" md="6" sm="12" xs="12">
                  <v-text-field
                    v-model="billingAddress.postal_code"
                    :label="$t('custom.zip') + ' *'"
                    outlined
                    :rules="[v => !!v || $t('errors.required_field')]"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="success" @click="saveBillingAddress()">
                {{ $t("custom.save") }}
              </v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
            <v-col cols="12">
              <v-divider class="mb-2"></v-divider>
            </v-col>
          </v-card>
        </v-col>
        <!-- Полированная карточка тарифа для balance-клиентов (не-MP: Tinkoff/Paddle/Paypro/Stripe-invoice).
             Паритет с MP-карточкой: статус + цена + метрики использования + смена тарифа.
             Список включённых модулей — только в выборе тарифа (не дублируем). Переиспользует mpUsage. -->
        <!-- Тариф (слева): имя + цена справа + поштучные тарифы чипами + смена тарифа -->
        <v-col cols="12" md="6" lg="5">
          <v-card flat class="tx-card" style="height:100%">
            <v-card-text class="pa-5 d-flex flex-column" style="height:100%">
              <div class="d-flex justify-space-between align-start">
                <div style="min-width:0">
                  <div class="text-overline grey--text mb-n1">{{ $t("plans.current_plan") }}</div>
                  <div class="text-h5 font-weight-medium text-truncate">{{ main_company.plan.name }}</div>
                  <v-chip v-if="main_company.plan.trial_end" class="mt-2" x-small color="info" outlined>
                    {{ $t("plans.trial_period_for") }}: {{ formatDate(main_company.plan.trial_end) }}
                  </v-chip>
                </div>
                <div class="text-right ml-3">
                  <div class="text-h5 font-weight-medium">{{ planPrice }}&nbsp;{{ main_company.plan.currency }}</div>
                  <div v-if="planPriceSuffixText" class="text-caption grey--text">{{ planPriceSuffixText }}</div>
                </div>
              </div>
              <div class="d-flex flex-wrap mt-4" style="gap:8px">
                <v-chip v-if="planMin" small label outlined>{{ $t("plans.min_payment_sum") }} · {{ planMin }} {{ main_company.plan.currency }}</v-chip>
                <v-chip v-if="company.whatsapp && main_company.plan.whatsapp_cost" small label outlined>WhatsApp · {{ main_company.plan.whatsapp_cost }} {{ main_company.plan.currency }}</v-chip>
                <v-chip v-if="company.telegram_on && main_company.plan.telegram_cost" small label outlined>Telegram · {{ main_company.plan.telegram_cost }} {{ main_company.plan.currency }}</v-chip>
                <v-chip v-if="company.max_enabled && main_company.plan.messenger_max_cost" small label outlined>MAX · {{ main_company.plan.messenger_max_cost }} {{ main_company.plan.currency }}</v-chip>
                <v-chip v-if="main_company.plan.fines_monitoring_cost > 0" small label outlined>{{ $t("plans.fines_cost") }} · {{ main_company.plan.fines_monitoring_cost }} {{ main_company.plan.currency }}</v-chip>
              </div>
              <div class="mt-auto pt-5">
                <v-btn block color="primary" @click="showChangePlanDialog = true">
                  <v-icon left>mdi-swap-horizontal</v-icon>{{ $t("plans.change_plan") }}
                </v-btn>
                <v-btn block text small class="mt-2"
                  :href="`https://rentprog.${$root.$i18n.locale == 'ru' ? 'ru' : 'com'}/${$root.$i18n.locale}/tariffs`" target="_blank">
                  <v-icon left small>mdi-open-in-new</v-icon>{{ $t("tariffs.tariffs") }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <!-- Использование (справа): 3 метра с иконками -->
        <v-col cols="12" md="6" lg="7">
          <v-card flat class="tx-card" style="height:100%">
            <v-card-text class="pa-5">
              <div class="text-overline grey--text mb-3">{{ $t("plans.usage") }}</div>
              <div v-for="m in mpUsage" :key="m.label" class="mb-4">
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="text-body-2 grey--text d-flex align-center">
                    <v-icon small class="mr-2">{{ m.icon }}</v-icon>{{ m.label }}
                    <v-icon v-if="m.over" x-small color="error" class="ml-1">mdi-alert</v-icon>
                  </span>
                  <span class="text-body-2 font-weight-medium" :class="m.over ? 'error--text' : ''">
                    {{ m.value }} <span class="grey--text">/ {{ m.limit }}</span>
                  </span>
                </div>
                <v-progress-linear :value="m.pct" :color="m.over ? 'error' : m.color" height="6" rounded></v-progress-linear>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- FT-074: витрина «что подключено и что по чём» (read-only, для всех клиентов) -->
      <billing-overview
        :main-company="main_company"
        :can-manage="canManageAddons"
        @changed="reloadBilling"
      />

      <v-flex v-if="isTinkoffPayment && $store.getters.getMainCompany && $store.getters.getMainCompany.currency == 'RUB'">
        <v-flex :class="isMobile ? '' : 'd-flex justify-start'">
          <h2 v-if="
            card_list &&
            card_list.length > 0 &&
            $store.getters.getMainCompany.rebill_id &&
            $store.getters.getMainCompany.card_id
          " class="ml-3 mt-3">
            {{ $t("transfers.your_cards") }}
          </h2>
        </v-flex>
        <v-simple-table class="mx-5" v-if="
          card_list &&
          card_list.length > 0 &&
          $store.getters.getMainCompany.rebill_id &&
          $store.getters.getMainCompany.card_id
        ">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">
                  {{ $t("custom.number_word") }}
                </th>
                <th class="text-center">
                  {{ $t("transfers.current_tooltip") }}
                </th>
                <th class="text-left"></th>
              </tr>
            </thead>
            <tbody>
              <tr :class="item.Status != 'A' ? 'red--text' : ''" v-for="item in card_list" :key="item.CardId">
                <td>{{ item.Pan }}</td>
                <td class="text-center">
                  <v-icon v-if="
                    item.CardId == $store.getters.getMainCompany.card_id
                  " v-tooltip="{
                    content: $t('transfers.current_tooltip'),
                    placement: 'bottom-center',
                    classes: ['info'],
                    targetClasses: ['it-has-a-tooltip'],
                    delay: {
                      show: 500,
                      hide: 500,
                    },
                  }">mdi-check</v-icon>
                </td>
                <td class="text-right">
                  <v-icon v-if="item.Status != 'D'" size="30" color="red darken-2" @click="deleteCard(item.CardId)"
                    v-tooltip="{
                      content: $t('transfers.delete_card_tooltip'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">mdi-delete-forever</v-icon>
                  <span v-else>{{ $t("transfers.deleted") }}</span>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-flex>
      <!-- FT-071: EUR Stripe-автоплатёж — витрина привязанной карты + отвязка (паритет с Tinkoff).
           Показываем ТОЛЬКО когда подключено; сам connect живёт в TopupDialog (без дублирования). -->
      <v-flex v-if="isStripeAutopayConnected" class="px-3 mb-2">
        <h2 class="mt-3 mb-2">{{ $t("transfers.stripe_autopay_title") }}</h2>
        <v-card flat class="pa-3 rm-stripe-card">
          <div class="d-flex align-center flex-wrap" style="gap: 8px">
            <v-icon color="success" class="mr-2">mdi-credit-card-check-outline</v-icon>
            <div class="flex-grow-1">
              <div v-if="stripeCardLabel" class="font-weight-medium">{{ stripeCardLabel }}</div>
              <div class="caption success--text">{{ $t("transfers.stripe_autopay_connected") }}</div>
              <div class="caption grey--text"
                v-if="main_company.stripe_rebill_limit && main_company.stripe_rebill_sum">
                {{ $t("transfers.rebill_limit_short") }}: {{ main_company.stripe_rebill_limit }} {{ planCurrency }}
                · {{ $t("transfers.rebill_sum_title") }}: {{ main_company.stripe_rebill_sum }} {{ planCurrency }}
              </div>
            </div>
            <v-btn text color="error" :loading="stripeAutopayLoading" @click="detachStripeAutopay()">
              <v-icon left small>mdi-credit-card-off-outline</v-icon>
              {{ $t("transfers.stripe_autopay_detach") }}
            </v-btn>
          </div>
        </v-card>
        <hr class="mb-2 mt-3" />
      </v-flex>
      <!-- Леджер операций: группировка по датам, строки-иконки, суммы цветом по знаку.
           Серверная пагинация сохранена (goPage/setPerPage → fetchTransactions). -->
      <v-card flat class="tx-card mx-3 mb-4 mt-4">
        <div class="d-flex align-center px-5 py-4">
          <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
          <span class="text-subtitle-1 font-weight-medium">{{ $t("tariffs.transfers") }}</span>
          <v-spacer></v-spacer>
          <!-- FT-147 REQ-05: акт за месяц — только тем, кто платил по счёту (has_tbank_invoices из /transfers) -->
          <v-btn v-if="showTbankAct" small text color="primary" @click="openTbankActDialog()">
            <v-icon small class="mr-1">mdi-file-sign</v-icon>
            {{ $t("transfers.tbank_act_button") }}
          </v-btn>
        </div>
        <v-progress-linear v-if="isTableLoading" indeterminate color="primary" height="2"></v-progress-linear>

        <div v-if="!transactions.length && !isTableLoading" class="px-5 py-8 text-center grey--text">
          {{ $t("custom.no_data_in_table") }}
        </div>

        <template v-for="g in groupedTransactions">
          <div :key="`h-${g.key}`" class="tx-day px-5 pt-3 pb-1 text-caption grey--text">{{ g.label }}</div>
          <div v-for="t in g.items" :key="t.id" class="tx-row d-flex align-center px-5 py-2">
            <div class="tx-row-ico mr-3"><v-icon small>{{ txIcon(t) }}</v-icon></div>
            <div class="flex-grow-1" style="min-width:0">
              <div class="text-body-2 text-truncate">{{ txTitle(t) }}</div>
              <div v-if="txSubtitle(t)" class="text-caption grey--text text-truncate">{{ txSubtitle(t) }}</div>
            </div>
            <v-btn v-if="t.payment_provider === 'paddle' && t.provider_transaction_id" icon small class="mr-1"
              @click.stop="downloadPaddleInvoice(t.provider_transaction_id)"
              :loading="downloadingInvoiceId === t.provider_transaction_id" :title="$t('transfers.download_invoice')">
              <v-icon small color="primary">mdi-file-pdf-box</v-icon>
            </v-btn>
            <v-btn v-if="tbankInvoicePdfUrl(t)" icon small class="mr-1" :href="tbankInvoicePdfUrl(t)" target="_blank" rel="noopener noreferrer"
              :title="$t('transfers.tbank_download_invoice')">
              <v-icon small :color="t.paid ? 'success' : 'primary'">mdi-file-pdf-box</v-icon>
            </v-btn>
            <v-btn v-if="['stripe', 'stripe_managed_payments'].includes(t.payment_provider) && t.provider_transaction_id" icon small class="mr-1"
              @click.stop="openStripeInvoice(t.provider_transaction_id)"
              :loading="downloadingInvoiceId === t.provider_transaction_id" :title="$t('transfers.view_invoice')">
              <v-icon small :color="t.paid ? 'success' : 'primary'">mdi-file-document-outline</v-icon>
            </v-btn>
            <div class="tx-amount ml-2" :class="t.sum >= 0 ? 'success--text' : ''">{{ formatSum(t) }}</div>
          </div>
        </template>

        <v-divider></v-divider>
        <div class="d-flex align-center flex-wrap justify-end px-5 py-3" style="gap:12px">
          <span class="text-caption grey--text">{{ $t("custom.elements_table") }}</span>
          <v-select :value="perPage" :items="[20, 50, 100]" dense hide-details outlined
            class="tx-perpage" @change="setPerPage"></v-select>
          <span class="text-caption grey--text">{{ currentPage }} {{ $t("custom.of") }} {{ totalPages }}</span>
          <v-btn icon small :disabled="currentPage <= 1 || isTableLoading" @click="goPage(currentPage - 1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn icon small :disabled="currentPage >= totalPages || isTableLoading" @click="goPage(currentPage + 1)">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </v-card>
    </v-card>
    <!-- FT-070: единый диалог пополнения (TopupDialog.vue) -->
    <topup-dialog v-model="showTransactionDialog" @paid="onTopupPaid" />
    <!-- FT-147 REQ-05: акт оказанных услуг за месяц (T-Bank Business OpenAPI) -->
    <v-dialog v-model="showTbankActDialog" max-width="480px">
      <v-card>
        <v-card-title class="text-h6">{{ $t("transfers.tbank_act_title") }}</v-card-title>
        <v-card-text>
          <v-menu v-model="showTbankActMonthMenu" :close-on-content-click="false" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field :value="tbankAct.month" :label="$t('transfers.tbank_act_month')" prepend-icon="mdi-calendar"
                readonly hide-details="auto" v-bind="attrs" v-on="on"></v-text-field>
            </template>
            <v-date-picker v-model="tbankAct.month" type="month" :max="tbankActMaxMonth" @input="showTbankActMonthMenu = false"></v-date-picker>
          </v-menu>
          <v-text-field v-model.trim="tbankAct.buyer.name" :label="$t('transfers.tbank_payer_name') + ' *'" dense outlined hide-details="auto" class="mt-4"></v-text-field>
          <v-row dense class="mt-1">
            <v-col cols="6"><v-text-field v-model.trim="tbankAct.buyer.inn" :label="$t('transfers.tbank_payer_inn') + ' *'" dense outlined hide-details="auto" inputmode="numeric"></v-text-field></v-col>
            <v-col cols="6"><v-text-field v-model.trim="tbankAct.buyer.kpp" :label="$t('transfers.tbank_payer_kpp')" dense outlined hide-details="auto" inputmode="numeric"
              :disabled="tbankActBuyerIsSoleProprietor"></v-text-field></v-col>
          </v-row>
          <v-text-field v-model.trim="tbankAct.buyer.email" :label="$t('transfers.tbank_payer_email') + ' *'" dense outlined hide-details="auto" type="email" class="mt-1"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showTbankActDialog = false">{{ $t("custom.cancel") }}</v-btn>
          <v-btn color="primary" :loading="tbankActLoading" :disabled="tbankActLoading" @click="tbankCreateAct()">{{ $t("transfers.tbank_act_generate") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="isTinkoffPayment" v-model="showRebillDialog" :retain-focus="false" persistent max-width="520px">
      <v-card>
        <v-card-title class="d-flex align-center px-6 pt-5 pb-1">
          <span class="text-h6 font-weight-medium">{{ $t("transfers.start_rebill_title") }}</span>
          <v-chip v-if="!hasActiveRebill" small outlined color="success" class="ml-3 font-weight-medium">
            0 ₽ · {{ $t("transfers.no_charge_chip") }}
          </v-chip>
        </v-card-title>

        <v-card-text class="px-6 pt-5 pb-2">
          <div class="d-flex" style="gap: 16px;">
            <v-text-field v-model.trim="rebill_limit" type="number" dense outlined hide-details="auto"
              :rules="rebillLimitRules"
              :suffix="planCurrency" :label="$t('transfers.rebill_limit_short')"></v-text-field>
            <v-text-field v-model.trim="rebill_sum" type="number" dense outlined hide-details="auto"
              :rules="rebillSumRules"
              :suffix="planCurrency" :label="$t('transfers.rebill_sum_title')"></v-text-field>
          </div>

          <v-expansion-panels flat tile class="rebill-terms mt-2">
            <v-expansion-panel>
              <v-expansion-panel-header class="px-0 py-0 caption grey--text text--darken-2" style="min-height: 40px;">
                {{ $t("transfers.rebill_terms_more") }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="caption grey--text text--darken-1">
                <p class="mb-2">{{ $t("transfers.add_card_info") }}</p>
                <p class="mb-2">{{ $t("transfers.rebill_sum") }} — {{ $t("transfers.rebill_type_sum") }}</p>
                <p class="mb-2">{{ $t("transfers.rebill_access_terms") }}</p>
                <p class="mb-2">{{ $t("transfers.rebill_sum_terms") }}</p>
                <p class="mb-0">
                  {{ $t("transfers.rebill_support") }}
                  <a href="mailto:admin@rentprog.com">admin@rentprog.com</a>
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <div class="caption mt-4">
            <a :href="rebillDocUrl('tariffs')" target="_blank">{{ $t("transfers.link_tariffs") }}</a>
            <span class="grey--text mx-2">·</span>
            <a :href="rebillDocUrl('agreement')" target="_blank">{{ $t("transfers.link_agreement") }}</a>
            <span class="grey--text mx-2">·</span>
            <a :href="rebillDocUrl('privacy')" target="_blank">{{ $t("transfers.link_privacy") }}</a>
          </div>

          <v-divider class="mt-4"></v-divider>
          <v-checkbox v-model="accept_rebill_1" hide-details dense class="rebill-consent mt-3"
            :label="$t('transfers.rebill_consent')"></v-checkbox>
        </v-card-text>

        <v-card-actions class="px-6 pt-1 pb-5">
          <v-btn color="success darken-1" depressed :disabled="!canSubmitRebill" @click="submitRebill()">
            {{ hasActiveRebill ? $t("transfers.rebill_save") : $t("transfers.start_recurrent") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeRebillDialog()">{{ $t("custom.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showChangePlanDialog" :retain-focus="false" max-width="1100" scrollable>
      <v-card>
        <v-card-title class="px-6 pt-5">{{ $t("plans.change_plan") }}</v-card-title>
        <v-card-text class="pb-2">
          <v-row>
            <v-col v-for="item in selectablePlans" :key="item.id" cols="12" sm="6" md="4" lg="3">
              <v-card outlined height="100%" class="d-flex flex-column"
                :style="isCurrentPlan(item) ? 'border:2px solid var(--v-primary-base)' : ''">
                <v-card-text class="d-flex flex-column flex-grow-1">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="text-h6 font-weight-medium">{{ item.name }}</span>
                    <v-chip v-if="isCurrentPlan(item)" x-small color="info" outlined>{{ $t("stripe_mp_lifecycle.current_short") }}</v-chip>
                  </div>
                  <div class="d-flex align-baseline flex-wrap">
                    <span class="text-h4 font-weight-medium">{{ planUnitMonthly(item) }}</span>
                    <span class="text-caption grey--text ml-1">{{ item.currency }} {{ planPriceSuffix(item) }}</span>
                  </div>
                  <!-- Высота строки зарезервирована всегда (даже у fix_plan без оценки),
                       чтобы дивайдер/иконки/сервисы были на одном уровне во всех карточках. -->
                  <div class="text-caption info--text mt-1" style="min-height:18px">
                    <template v-if="item.payment_type === 'pay_as_you_go'">{{ $t("stripe_mp_lifecycle.est_per_month", { sum: planEstMonthly(item), currency: item.currency }) }} · {{ $t("stripe_mp_lifecycle.min_label").toLowerCase() }} {{ planMinMonthly(item) }}</template>
                  </div>
                  <v-divider class="my-3"></v-divider>
                  <div class="text-body-2 mb-3">
                    <span class="mr-3" :title="$t('plans.vehicles_qty')"><v-icon small class="mr-1 grey--text">mdi-car</v-icon>{{ item.cars_limit }}</span>
                    <span class="mr-3" :title="$t('plans.users_qty')"><v-icon small class="mr-1 grey--text">mdi-account-group</v-icon>{{ item.users_limit }}</span>
                    <span :title="$t('plans.branches_qty')"><v-icon small class="mr-1 grey--text">mdi-office-building</v-icon>{{ item.branches_limit }}</span>
                  </div>
                  <v-expansion-panels flat class="mb-3">
                    <v-expansion-panel>
                      <v-expansion-panel-header class="px-0 py-1 text-body-2">{{ $t("plans.included_services") }}</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <div v-for="s in item.included_services" :key="s" class="text-caption d-flex align-center mb-1">
                          <v-icon x-small color="success" class="mr-1">mdi-check</v-icon>{{ translateService(s) }}
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                  <v-btn v-if="isCurrentPlan(item)" disabled style="width:100%" class="mt-auto">{{ $t("stripe_mp_lifecycle.current_plan_short") }}</v-btn>
                  <v-btn v-else color="success" style="width:100%" class="mt-auto" :loading="isLoadingChangePlan" @click="changePlan(item.id)">{{ $t("stripe_mp_lifecycle.select_btn") }}</v-btn>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="text-center">
          <v-spacer></v-spacer>
          <v-btn text @click="showChangePlanDialog = false">{{ $t("custom.close") }}</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Диалог подтверждения покупки -->
    <v-dialog v-model="showPurchaseConfirmDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h5">
          {{ $t("custom.you_sure_title") }}
        </v-card-title>
        
        <v-card-text>
          <div class="text-body-1">
            {{ $t("transfers.in_app_purchase_question", { 
              payment_sum: purchaseConfirmData.payment_sum, 
              payment_currency: purchaseConfirmData.payment_currency, 
              top_up_sum: purchaseConfirmData.top_up_sum, 
              top_up_currency: purchaseConfirmData.top_up_currency 
            }) }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="cancelPurchase">
            {{ $t("custom.no") }}
          </v-btn>
          <v-btn color="success darken-1" text @click="confirmPurchase">
            {{ $t("custom.yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </span>
</template>
<script>
import { isSoleProprietor, defaultRequisites, requisitesErrorKey, requisitesPayload, tbankLedgerStatusKey, tbankLedgerChipColor } from "@/utils/tbankInvoiceQr";
import moment from "moment";
import { setMomentLocale } from "@/utils/localeInfra";
import "moment/locale/ru";
import "moment/locale/en-gb";
// Stripe SDK was removed - now using invoice-only payments
import purchaseService from '@/services/purchaseService';
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
// moment.locale("ru");
// import sha256 from "js-sha256";
// import sha256 from "crypto-js/sha256"
import PullToRefresh from "pulltorefreshjs";
import { countries } from "./../../countries";

import TopupDialog from "@/components/TopupDialog.vue"; // FT-070
import BillingOverview from "@/components/dashboard/transactions/BillingOverview.vue"; // FT-074
import { isStripeMpClient, isMpPreSubscription, isMpProvider, mpSubscriptionStatus, mpMonthlyEstimate, monthlyMultiplier, roundMoney, formatLedgerAmount, cancelConfirmText, changeConfirmText, downgradeMessage, downgradeUsersMessage, errorTextKey } from "@/helpers/mpLifecycle"; // FT-073

export default {
  components: {
    TopupDialog, // FT-070: единый диалог пополнения
    BillingOverview, // FT-074: витрина «что подключено / что по чём»
  },
  data() {
    return {
      // FT-147: акт за месяц (T-Bank)
      hasTbankInvoices: false,
      showTbankActDialog: false,
      showTbankActMonthMenu: false,
      tbankActLoading: false,
      tbankAct: { month: "", buyer: { name: "", inn: "", kpp: "", email: "" } },
      transactions: [],
      // Серверная пагинация таблицы транзакций (бэкенд: page/per_page/sort_by/sort_desc → total_count).
      totalCount: 0,
      isTableLoading: false,
      // Полный options-объект, чтобы v-data-table не перетёр его на mount (→ не было лишнего fetch).
      tableOptions: {
        page: 1, itemsPerPage: 20, sortBy: ["id"], sortDesc: [true],
        groupBy: [], groupDesc: [], multiSort: false, mustSort: false,
      },
      card_list: [],
      accept_rebill: false,
      accept_rebill_1: false,
      rebill_type: "Sum",
      rebill_limit: 1000,
      rebill_sum: 1000,
      cardBindingPolling: false,
      stripeAutopayLoading: false,
      showTransactionDialog: false,
      showRebillDialog: false,
      sum: 0,
      in_app_purchase_product: null,
      in_app_purchase_eur: 0,
      in_app_purchase_currency: null,
      in_app_purchase_price: null,
      in_app_purchase_start: false,
      showPurchaseConfirmDialog: false,
      purchaseConfirmData: {
        payment_sum: 0,
        payment_currency: '',
        top_up_sum: 0,
        top_up_currency: ''
      },
      headers: [
        { text: this.$t("custom.number"), align: "center", value: "id" },
        {
          text: this.$t("custom.created_at"),
          value: "created_at",
          align: "center",
        },
        { text: this.$t("custom.amount"), value: "sum", align: "center" },
        { text: this.$t("custom.status"), value: "state", align: "center" },
        {
          text: this.$t("custom.operation"),
          value: (item) => {
            if (item.sum < 0) {
              return false;
            } else {
              return true;
            }
          },
          align: "center",
          sortable: false, // вычисляемая колонка — серверной сортировки нет
        },
        { text: this.$t("custom.description"), value: "description", align: "center", sortable: false },
      ],
      pagination: { sortBy: "created_at", descending: true },
      search: null,
      plans_tab: 0,
      showChangePlanDialog: false,
      isMobile: false,
      isLoadingSubscribeToPaidTariff: false,
      isLoadingChangePlan: false,
      isLoadingMpCancel: false, // FT-073
      isLoadingMpCheckout: false, // FT-073
      isLoadingMpUpdateCard: false, // смена карты MP (Stripe Billing Portal)
      isLoading: true,
      error: "",
      countries: countries,
      billingAddress: {
        country_code: null,
        city: "",
        first_line: "",
        postal_code: ""
      },
      downloadingInvoiceId: null,
      // Показать Paddle форму для физлиц (когда VAT не компании)
    };
  },
  mounted() {
    let self = this;
    const ptr = PullToRefresh.init({
      mainElement: "body",
      distIgnore: 100,
      instructionsPullToRefresh: this.$t("custom.pull_to_refresh"),
      instructionsReleaseToRefresh: this.$t("custom.release_to_refresh"),
      instructionsRefreshing: this.$t("custom.refreshing"),
    });
    
    // Paddle инициализируется после загрузки main_company в created()
    
    // Обновляем данные при возврате на вкладку: Stripe/Paddle (оплата в новой вкладке) и
    // FT-147 — рублёвые аккаунты со счётами T-Bank (оплата снаружи, зачисление по опросу).
    if (this.isStripePayment || this.isPaddlePayment || this.isTinkoffPayment) {
      this._visibilityHandler = () => this.handleVisibilityChange();
      document.addEventListener('visibilitychange', this._visibilityHandler);
    }
  },
  beforeDestroy() {
    this.cardBindingPolling = false;
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
    }
  },
  created() {
    // moment Locale
    setMomentLocale(this.$i18n.locale);
    if (this.$store.getters.isLoggedIn) {
      this.isLoading = true;
      let self = this;
      // FT-077 редизайн: леджер больше не v-data-table → initial-fetch транзакций явно.
      this.fetchTransactions(this.tableOptions);
      this.axios
        .get(`/api/v1/company`, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          this.$store.commit("setCompany", { company: response.data });
          console.log(response.data);
          this.billingAddress = response.data.billing_address || { country_code: null, city: null, first_line: null, postal_code: null };
          this.axios
            .get(`api/v1/main_companies/${response.data.main_company_id}`, {
              headers: {
                Authorization: this.$store.getters.getAuthToken,
              },
            })
            .then((response_main_company) => {
              this.$store.commit("setMainCompany", response_main_company.data);
              // FT-070: Paddle-инициализацию и _ptxn-возврат теперь владеет TopupDialog.vue
              // (модульный гард от двойного срабатывания) — здесь не дублируем.
            })
            .catch((error) => {
              this.isLoading = false;
              this.setError(error, this.$t("errors.main_company_data"));
              console.log(error);
            });
        })
        .catch((error) => {
          this.isLoading = false;
          this.setError(error, this.$t("errors.company_data"));
          console.log(error);
        });
      // Транзакции грузит v-data-table через @update:options → fetchTransactions (серверная
      // пагинация); initial-fetch срабатывает на mount таблицы. card_list приходит с page 1.
    } else {
      this.$store.commit("unsetCurrentUser");
    }
  },
  watch: {
    accept_rebill_1() {
      if (this.accept_rebill_1) {
        this.accept_rebill = true;
      } else {
        this.accept_rebill = false;
      }
    },
  },
  computed: {
    user_email() {
      return this.$store.getters.getCurrentUser?.email || "";
    },
    // FT-074 DEC-32: self-service add-on управление — только владельцы биллинга
    // (superadmin/admin), зеркалит серверный ролевой гейт CompanyAddonsController.
    canManageAddons() {
      return ["superadmin", "admin"].includes(this.user_role);
    },
    currency() {
      if (this.$store.getters.getMainCompany && this.$store.getters.getMainCompany.currency) {
        return this.$store.getters.getMainCompany.currency;
      } else {
        return "EUR";
      }
    },
    paymentProviderPreference() {
      return this.$store.getters.getMainCompany?.payment_provider_preference || 'tinkoff';
    },
    isPaddlePayment() {
      return this.paymentProviderPreference === 'paddle';
    },
    isTinkoffPayment() {
      return this.paymentProviderPreference === 'tinkoff';
    },
    isStripePayment() {
      return this.paymentProviderPreference === 'stripe';
    },
    // FT-073: онбордённый MP-клиент (провайдер MP + заведённая подписка) — показываем cancel/change через Stripe.
    isStripeMpClient() {
      return isStripeMpClient(this.main_company);
    },
    // FT-073: MP назначен, но подписки ещё нет (продуктовый триал) — смена тарифа бесплатна (€0, DB-only).
    isMpPreSubscription() {
      return isMpPreSubscription(this.main_company);
    },
    // FT-073: показывать CTA «Оформить подписку» когда подписки нет (триал) ИЛИ она отменена
    // (sub_id есть, status=canceled → клиент переподписывается; stripe_mp_checkout создаст новую).
    mpNeedsSubscribe() {
      return (
        this.isMpPreSubscription ||
        (this.main_company && this.main_company.stripe_mp_status === "canceled")
      );
    },
    // FT-073: месячная смета (€) для маркетинг-карточки — round(техника × цена × ×30/×1), не ниже мин.
    mpMonthlyEstimate() {
      return mpMonthlyEstimate(this.main_company);
    },
    // FT-073: месячная цена за единицу (суточная × 30 для day_period), округление по валюте.
    mpUnitPriceMonthly() {
      const p = this.main_company && this.main_company.plan;
      if (!p) return 0;
      return roundMoney((Number(p.price) || 0) * monthlyMultiplier(p), p.currency);
    },
    // FT-073: месячный минимум (суточный × 30 для day_period), округление по валюте.
    mpMinMonthly() {
      const p = this.main_company && this.main_company.plan;
      if (!p) return 0;
      return roundMoney((Number(p.min_payment_sum) || 0) * monthlyMultiplier(p), p.currency);
    },
    // FT-073: дата следующего списания (конец текущего периода). Только для активной/просроченной
    // подписки — на отменённой/триале бэкенд держит null (списания нет).
    mpNextCharge() {
      const mc = this.main_company;
      if (!mc || !mc.stripe_mp_current_period_end || mc.stripe_mp_status === "canceled") return null;
      return this.formatDate(mc.stripe_mp_current_period_end);
    },
    // FT-073: тарифы для диалога выбора — только валюты компании (убирает RUB-дубли у EUR-клиента).
    // active/hided уже отфильтрованы сервером (StandartPlan.where(for_market:'all', hided:false)).
    selectablePlans() {
      const plans = (this.main_company && this.main_company.standart_plans) || [];
      const cur = (this.main_company && this.main_company.plan && this.main_company.plan.currency) ||
        (this.main_company && this.main_company.currency);
      return plans.filter((p) => p.currency === cur);
    },
    // FT-073: метрики использования (техника/сотрудники/филиалы) с прогресс-барами; over → красным.
    mpUsage() {
      const mc = this.main_company;
      const p = mc && mc.plan;
      if (!p) return [];
      const meter = (label, value, limit, color, icon) => {
        const v = Number(value) || 0;
        const lim = Number(limit) || 0;
        return { label, value: v, limit: lim, over: v >= lim, color, icon, pct: lim > 0 ? Math.min(100, Math.round((v / lim) * 100)) : 0 };
      };
      return [
        meter(this.$t("stripe_mp_lifecycle.usage_units"), mc.active_cars_count, p.cars_limit, "success", "mdi-car"),
        meter(this.$t("stripe_mp_lifecycle.usage_users"), mc.active_users_count, mc.effective_users_limit != null ? mc.effective_users_limit : p.users_limit, "info", "mdi-account-group"),
        meter(this.$t("stripe_mp_lifecycle.usage_branches"), mc.companies_count, p.branches_limit, "info", "mdi-office-building"),
      ];
    },
    // Balance-карточка (не-MP провайдеры): МЕСЯЧНАЯ цена за единицу (суточная × 30 для day_period),
    // округление по валюте — на сайте/в тарифах указываем за месяц, не за сутки. free_plan → 0.
    planPrice() {
      const p = this.main_company && this.main_company.plan;
      if (!p) return 0;
      if (p.payment_type === "free_plan") return 0;
      return roundMoney((Number(p.price) || 0) * monthlyMultiplier(p), p.currency);
    },
    // Суффикс цены: всегда «за месяц» (pay_as_you_go — за единицу техники; fix_plan — фикс).
    planPriceSuffixText() {
      const p = this.main_company && this.main_company.plan;
      if (!p || p.payment_type === "free_plan") return "";
      return p.payment_type === "fix_plan"
        ? this.$t("plans.price_per_month_fix")
        : this.$t("plans.price_per_month");
    },
    // Минимальный месячный платёж — только для посуточного pay_as_you_go (паритет со старым блоком).
    planMin() {
      const p = this.main_company && this.main_company.plan;
      if (!p) return null;
      if (p.payment_period === "day_period" && p.payment_type === "pay_as_you_go" && Number(p.min_payment_sum) > 0) {
        return roundMoney((Number(p.min_payment_sum) || 0) * monthlyMultiplier(p), p.currency);
      }
      return null;
    },
    // Цвет верхней полосы карточки: триал → info, проблема с балансом → error, иначе → success.
    planAccent() {
      const p = this.main_company && this.main_company.plan;
      if (p && p.trial_end) return "info";
      return this.balanceHealthy ? "success" : "error";
    },
    // Здоровье баланса для статус-чипа/полосы балансовой карточки: payer_state==3 (блок)
    // или отрицательный баланс → не «в порядке».
    balanceHealthy() {
      const mc = this.main_company;
      if (!mc) return true;
      if (Number(mc.payer_state) === 3) return false;
      return parseFloat(mc.balance) >= 0;
    },
    // FT-073 (holistic-ревью P2): кнопку отмены показываем только на НЕ-отменённой подписке.
    // sub_id НЕ чистится при отмене (иначе клиент стал бы mp_pre_subscription? → смена ушла бы в
    // триал-€0-ветку), поэтому гейтим по статусу, а не по наличию подписки.
    canCancelMp() {
      return this.isStripeMpClient && this.main_company && this.main_company.stripe_mp_status !== "canceled";
    },
    // FT-073: провайдер = MP (с подпиской или без) — заменяет балансовый top-up на управление подпиской.
    isMpClient() {
      return isMpProvider(this.main_company);
    },
    // FT-073: статус MP-подписки → текст/цвет чипа.
    mpStatusText() {
      return this.$t(mpSubscriptionStatus(this.main_company && this.main_company.stripe_mp_status).key);
    },
    mpStatusColor() {
      return mpSubscriptionStatus(this.main_company && this.main_company.stripe_mp_status).color;
    },
    // FT-073: подсказка месячной суммы по типу тарифа (pay_as_you_go — по числу авто; fix_plan — flat).
    mpPlanMonthlyHint() {
      const p = this.main_company && this.main_company.plan;
      if (!p) return "";
      if (p.payment_type === "fix_plan") return this.$t("stripe_mp_lifecycle.monthly_fix", { price: p.price });
      if (p.payment_type === "pay_as_you_go") return this.$t("stripe_mp_lifecycle.monthly_payg", { price: p.price, min: p.min_payment_sum });
      return "";
    },
    // FT-071: EUR Stripe-автоплатёж подключён — есть сохранённый PaymentMethod (паритет с Tinkoff rebill_id)
    isStripeAutopayConnected() {
      return !!(this.main_company && this.main_company.stripe_payment_method_id);
    },
    // FT-071: «лицо» привязанной карты — «Visa •••• 4242» (brand/last4 с бэка). null, если карта
    // привязана до захвата brand/last4 → строка карты не рисуется (статус «подключён» остаётся).
    stripeCardLabel() {
      const mc = this.main_company || {};
      const last4 = mc.stripe_card_last4;
      if (!last4) return null;
      const brand = mc.stripe_card_brand;
      const pretty = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "";
      return pretty ? `${pretty} •••• ${last4}` : `•••• ${last4}`;
    },
    // FT-067: активная привязка карты есть → диалог в режиме правки (без перепривязки, без 0₽-чипа)
    hasActiveRebill() {
      const mainCompany = this.$store.getters.getMainCompany;
      return !!(mainCompany && mainCompany.rebill_id);
    },
    // FT-067: валюта тарифа для подписей сумм
    planCurrency() {
      return (this.main_company.plan && this.main_company.plan.currency) || this.main_company.currency || "";
    },
    // FT-150 REQ-05: нижняя граница суммы автоплатежа — main_company.topup_floor
    // (пол max(min_topup_sum, 3 × дневное списание), не выше 15 000); без поля — min_topup_sum тарифа.
    // Бэкенд-422 rebill_sum_below_floor показывается через setError (текст с суммой).
    minTopupSum() {
      const mc = this.main_company || {};
      if (mc.topup_floor !== undefined && mc.topup_floor !== null) return Number(mc.topup_floor) || 0;
      return (mc.plan && mc.plan.min_topup_sum) || 0;
    },
    // FT-067: дефолт суммы пополнения ≈ месяц (min_payment_sum * 30), в рамках [пол, 15000 — кап RebillJob]
    defaultRebillSum() {
      const plan = this.main_company.plan || {};
      const minTopup = this.minTopupSum;
      let suggested = Math.round((plan.min_payment_sum || 0) * 30);
      suggested = Math.max(suggested, minTopup);
      suggested = Math.min(suggested, 15000);
      return suggested > 0 ? suggested : 1000;
    },
    // FT-067: Vuetify-rules порога баланса (обязательное, > 0)
    rebillLimitRules() {
      return [
        (v) => (v !== "" && v !== null && v !== undefined) || this.$t("errors.required_field"),
        (v) => parseFloat(v) > 0 || this.$t("transfers.field_positive"),
      ];
    },
    // FT-067: Vuetify-rules суммы пополнения (обязательное, ≥ min_topup, ≤ 15000)
    rebillSumRules() {
      return [
        (v) => (v !== "" && v !== null && v !== undefined) || this.$t("errors.required_field"),
        (v) => parseFloat(v) >= this.minTopupSum || this.$t("transfers.rebill_min_hint", { sum: this.minTopupSum, currency: this.planCurrency }),
        (v) => parseFloat(v) <= 15000 || this.$t("transfers.rebill_max_hint", { sum: 15000, currency: this.planCurrency }),
      ];
    },
    // FT-067: кнопка активна только когда все условия выполнены (согласие + валидные суммы)
    canSubmitRebill() {
      if (!this.accept_rebill) return false;
      const limit = parseFloat(this.rebill_limit);
      const sum = parseFloat(this.rebill_sum);
      return limit > 0 && sum > 0 && sum >= this.minTopupSum && sum <= 15000;
    },
    isInvoiceClient() {
      return this.paymentProviderPreference === 'paddle' &&
             this.$store.getters.getMainCompany?.plan?.billing_collection_method === 'invoice';
    },
    nextPaymentDate() {
      // Вычисляем дату следующего платежа на основе дня регистрации компании
      if (!this.main_company || !this.main_company.plan) return null;
      
      const plan = this.main_company.plan;
      
      // Если есть payment_date в плане - используем его
      if (plan.payment_date) {
        return moment(plan.payment_date).format('DD.MM.YYYY');
      }
      
      // Иначе вычисляем на основе created_at компании
      if (this.main_company.created_at) {
        const createdAt = moment(this.main_company.created_at);
        const today = moment();
        const paymentDay = createdAt.date(); // День месяца регистрации
        
        // Находим следующую дату с этим днём
        let nextDate = moment().date(paymentDay);
        if (nextDate.isSameOrBefore(today, 'day')) {
          nextDate.add(1, 'month');
        }
        // Если день больше чем в месяце, берём последний день месяца
        if (paymentDay > nextDate.daysInMonth()) {
          nextDate.date(nextDate.daysInMonth());
        }
        return nextDate.format('DD.MM.YYYY');
      }
      
      return null;
    },
    sortedCountries() {
      // Преобразуем и сортируем страны по названию на текущем языке
      const isRu = this.$i18n.locale === 'ru';
      return this.countries
        .map(c => ({
          text: isRu ? (c.russian || c.name) : c.name,
          value: c.code
        }))
        .sort((a, b) => a.text.localeCompare(b.text, this.$i18n.locale));
    },
    userMustChangeTariffPlan() {
      if (this.$store.getters.getMainCompany) {
        if (this.$store.getters.getMainCompany.plan && this.$store.getters.getMainCompany.plan.id) {
          if (this.$store.getters.getMainCompany.plan.name == "Старт" || this.$store.getters.getMainCompany.plan.name == "Start") {
            if (this.$store.getters.getMainCompany.plan.trial_end && moment(this.$store.getters.getMainCompany.plan.trial_end).isAfter(moment())) {
              return false;
            } else {
              return moment(this.$store.getters.getMainCompany.created_at).isBefore(moment().subtract(7, "day")) ? true : false;
            }
          } else {
            return false;
          }
        } else {
          return false
        }
      } else {
        return false;
      }
    },
    daily_payments() {
      if (
        this.$store.getters.getMainCompany &&
        this.$store.getters.getMainCompany.tariff != "Старт" &&
        this.$store.getters.getMainCompany.tariff != "Безлимит"
      ) {
        let daily_payment = 0;
        if (this.$store.getters.getMainCompany.currency == "RUB") {
          if (this.$store.getters.getMainCompany.tariff == "Стандарт") {
            daily_payment = 150.0;
          } else if (this.$store.getters.getMainCompany.tariff == "Стабильный") {
            daily_payment = 135.0;
          } else if (this.$store.getters.getMainCompany.tariff == "Средний") {
            daily_payment = 120.0;
          } else if (
            this.$store.getters.getMainCompany.tariff == "Профессионал"
          ) {
            daily_payment = 105.0;
          } else if (this.$store.getters.getMainCompany.tariff == "Эксперт") {
            daily_payment = 90.0;
          } else if (
            this.$store.getters.getMainCompany.tariff == "Индивидуальный"
          ) {
            daily_payment = 78.0;
          } else if (
            this.$store.getters.getMainCompany.tariff == "Индивидуальный+"
          ) {
            daily_payment =
              parseFloat(this.$store.getters.getMainCompany.manual_price) *
              30.0;
          }
        } else {
          if (this.$store.getters.getMainCompany.tariff == "Стандарт") {
            daily_payment = parseFloat(1.5).toFixed(2);
          } else if (this.$store.getters.getMainCompany.tariff == "Стабильный") {
            daily_payment = parseFloat(1.4).toFixed(2);
          } else if (this.$store.getters.getMainCompany.tariff == "Средний") {
            daily_payment = parseFloat(1.3).toFixed(2);
          } else if (
            this.$store.getters.getMainCompany.tariff == "Профессионал"
          ) {
            daily_payment = parseFloat(1.2).toFixed(3);
          } else if (this.$store.getters.getMainCompany.tariff == "Эксперт") {
            daily_payment = parseFloat(1.1).toFixed(3);
          } else if (
            this.$store.getters.getMainCompany.tariff == "Индивидуальный"
          ) {
            daily_payment = parseFloat(1.0).toFixed(3);
          } else if (
            this.$store.getters.getMainCompany.tariff == "Индивидуальный+"
          ) {
            daily_payment = parseFloat(
              this.$store.getters.getMainCompany.manual_price * 30.0
            ).toFixed(3);
          }
        }
        return daily_payment;
      } else {
        return 0;
      }
    },
    isIos() {
      return Capacitor.getPlatform().toLowerCase() == "ios" ? true : false;
    },
    isAndroid() {
      return Capacitor.getPlatform().toLowerCase() == "android" ? true : false;
    },
    // FT-147: акт доступен рублёвым Tinkoff-аккаунтам в вебе
    showTbankAct() {
      return this.hasTbankInvoices && this.isTinkoffPayment && !!this.main_company && this.main_company.currency === "RUB" && !this.isNative;
    },
    tbankActMaxMonth() {
      return moment().format("YYYY-MM");
    },
    tbankActBuyerIsSoleProprietor() {
      return isSoleProprietor(this.tbankAct.buyer.inn);
    },
    isNative() {
      return Capacitor.isNativePlatform();
    },
    main_company() {
      return this.$store.getters.getMainCompany || {};
    },
    company() {
      return this.$store.getters.getCompany && this.$store.getters.getCompany.company ? this.$store.getters.getCompany.company : {};
    },
    // FT-077 редизайн: пагинация леджера (серверная) + группировка текущей страницы по датам.
    perPage() {
      return this.tableOptions.itemsPerPage > 0 ? this.tableOptions.itemsPerPage : 20;
    },
    currentPage() {
      return this.tableOptions.page || 1;
    },
    totalPages() {
      return Math.max(1, Math.ceil((Number(this.totalCount) || 0) / this.perPage));
    },
    groupedTransactions() {
      const groups = [];
      const map = {};
      (this.transactions || []).forEach((t) => {
        const key = moment.parseZone(t.created_at).format("YYYY-MM-DD");
        if (!map[key]) {
          map[key] = { key, label: this.txDateLabel(t.created_at), items: [] };
          groups.push(map[key]);
        }
        map[key].items.push(t);
      });
      return groups;
    },
  },
  methods: {
    
    // FT-070: только открытие — email-гард, префилл (Stripe-адрес/VAT/due-date, автоплатёж)
    // делает TopupDialog.vue в onOpen.
    transactionStart() {
      this.showTransactionDialog = true;
    },
    // FT-074 DEC-32: после self-service enable/disable add-on'а перезагрузить main_company
    // (обновлённые company_addons + баланс) И список транзакций (новое consumption-списание).
    reloadBilling() {
      const mainCompanyId = this.$store.getters.getMainCompany?.id;
      if (mainCompanyId) {
        this.axios
          .get(`api/v1/main_companies/${mainCompanyId}`, {
            headers: { Authorization: this.$store.getters.getAuthToken },
          })
          .then((response) => this.$store.commit("setMainCompany", response.data))
          .catch((error) => console.error("Failed to refresh main_company:", error));
      }
      this.onTopupPaid();
    },
    // FT-070: после успешной оплаты/счёта из TopupDialog перезагрузить список транзакций.
    // Новая транзакция — самая свежая → сброс на 1-ю страницу (новые сверху) + гарантированный рефетч.
    onTopupPaid() {
      this.tableOptions.page = 1;
      this.fetchTransactions(this.tableOptions);
    },
    // FT-077 редизайн: смена страницы леджера — серверная пагинация (тот же fetchTransactions).
    goPage(page) {
      const p = Math.min(Math.max(1, page), this.totalPages);
      if (p === this.currentPage) return;
      this.tableOptions.page = p;
      this.fetchTransactions(this.tableOptions);
    },
    setPerPage(n) {
      this.tableOptions.itemsPerPage = Number(n) || 20;
      this.tableOptions.page = 1;
      this.fetchTransactions(this.tableOptions);
    },
    // Заголовок группы по дате (локаль moment уже выставлена в created).
    txDateLabel(date) {
      return moment.parseZone(date).format("D MMMM");
    },
    // Иконка строки: пополнение / по типу списания (whatsapp/telegram/юзер/штраф).
    // ─────────── FT-147: счёт / акт T-Bank ───────────
    tbankInvoicePdfUrl(t) {
      return (t && t.payment_provider === "tbank_invoice" && t.payer_details && t.payer_details.invoice && t.payer_details.invoice.pdf_url) || null;
    },
    openTbankActDialog() {
      // Прошлый месяц в локальном времени (toISOString — UTC, 1-го числа съезжает на месяц).
      if (!this.tbankAct.month) this.tbankAct.month = moment().subtract(1, "month").format("YYYY-MM");
      const defaults = defaultRequisites(this.company, this.$store.getters.getCurrentUser);
      Object.keys(defaults).forEach((k) => {
        if (!this.tbankAct.buyer[k]) this.tbankAct.buyer[k] = defaults[k];
      });
      this.showTbankActDialog = true;
    },
    tbankCreateAct() {
      const errorKey = requisitesErrorKey(this.tbankAct.buyer);
      if (errorKey) return this.$notify(this.$t(errorKey), "", "warning");
      const buyer = requisitesPayload(this.tbankAct.buyer);
      this.tbankActLoading = true;
      // Окно под PDF открываем ДО запроса (в обработчике клика) — иначе после async-ответа
      // его режет popup-блокер (Safari — всегда); паттерн как у оплаты через банк-форму ниже.
      const actWindow = this.isNative ? null : window.open("", "_blank", "noopener,noreferrer");
      this.axios
        .post(
          "/api/v1/tbank_create_act",
          { act: { month: this.tbankAct.month, buyer } },
          { headers: { Authorization: this.$store.getters.getAuthToken } }
        )
        .then((response) => {
          this.showTbankActDialog = false;
          this.$notify(this.$t("custom.success"), this.$t("transfers.tbank_act_created"), "success");
          const url = response.data.pdf_url;
          if (!url) {
            if (actWindow) actWindow.close();
            this.$notify(this.$t("errors.error"), "", "warning");
          } else if (actWindow) {
            actWindow.location.href = url;
          } else {
            Browser.open({ url }).catch(() => {});
          }
        })
        .catch((error) => {
          if (actWindow) actWindow.close();
          const data = error.response && error.response.data;
          if (data && data.error_code === "no_consumption") {
            this.$notify(this.$t("transfers.tbank_act_no_consumption"), "", "warning");
          } else {
            this.setError(error, (data && data.error) || this.$t("errors.error"));
          }
        })
        .finally(() => (this.tbankActLoading = false));
    },
    txIcon(t) {
      if (t.sum >= 0) return "mdi-arrow-down-left";
      const d = (t.description || "").toLowerCase();
      if (d.includes("whatsapp")) return "mdi-whatsapp";
      if (d.includes("telegram")) return "mdi-send";
      if (d.includes("пользовател") || d.includes("user")) return "mdi-account-plus";
      if (d.includes("штраф") || d.includes("fine")) return "mdi-file-document-alert-outline";
      return "mdi-arrow-up-right";
    },
    // Подпись направления (пополнение / списание).
    txDirection(t) {
      return t.sum >= 0 ? this.$t("transfers.op_credit") : this.$t("transfers.op_debit");
    },
    // Заголовок строки: локализуем системные описания доп-услуг «RentProg <код> [— период]»
    // в человекочитаемую метку (billing_overview.addon_<код>, как в BillingOverview); период
    // отбрасываем (дата уже в группе). Прочие описания — как есть; пусто → направление.
    txTitle(t) {
      const desc = t.description || "";
      const m = desc.match(/^RentProg\s+(\S+)/);
      if (m) {
        const key = `billing_overview.addon_${m[1]}`;
        const label = this.$t(key);
        if (label !== key) return label;
      }
      return desc || this.txDirection(t);
    },
    // Подпись строки: направление (только если есть описание) · статус (скрываем «?») · промокод.
    txSubtitle(t) {
      const parts = [];
      if (t.description) parts.push(this.txDirection(t));
      const state = this.returnState(t.state, t.paid, t);
      if (state && state !== "?") parts.push(state);
      if (t.promo_code) parts.push(t.promo_code);
      return parts.join(" · ");
    },
    // Сумма со знаком + валюта (пополнение с «+»).
    // Округление до копеек — в formatLedgerAmount (см. комментарий там): без него
    // суточное списание с дробной ценой печаталось как «-66.66666666666667 RUB».
    formatSum(t) {
      const amount = t.subtotal_amount != null ? t.subtotal_amount : t.sum;
      const cur = this.$store.getters.getMainCompany ? this.$store.getters.getMainCompany.currency : "";
      const sign = t.sum >= 0 ? "+" : "";
      return `${sign}${formatLedgerAmount(amount)} ${cur}`;
    },
    // Серверная пагинация: грузит ОДНУ страницу транзакций по options таблицы
    // (page/itemsPerPage/sortBy/sortDesc → page/per_page/sort_by/sort_desc). Вызывается из
    // @update:options таблицы (initial на mount + при смене страницы/сортировки). card_list
    // (сохранённые карты) авторитетен только на page 1 — на остальных бэкенд отдаёт [].
    fetchTransactions(options) {
      const opts = options || this.tableOptions || {};
      const page = opts.page || 1;
      const perPage = opts.itemsPerPage > 0 ? opts.itemsPerPage : 200;
      const params = { page, per_page: perPage };
      if (opts.sortBy && opts.sortBy.length) {
        params.sort_by = opts.sortBy[0];
        params.sort_desc = opts.sortDesc && opts.sortDesc[0] ? "true" : "false";
      }
      this.isTableLoading = true;
      this.axios
        .get("/api/v1/transfers", { params, headers: { Authorization: this.$store.getters.getAuthToken } })
        .then((response) => {
          this.transactions = response.data.transfers || [];
          this.hasTbankInvoices = !!response.data.has_tbank_invoices;
          this.totalCount =
            response.data.total_count != null ? response.data.total_count : this.transactions.length;
          // card_list приходит только с page 1 — на других страницах не затираем существующий.
          if (page === 1 && response.data.card_list) {
            this.card_list = response.data.card_list;
          }
        })
        .catch((error) => this.setError(error, this.$t("errors.counts_load")))
        .finally(() => {
          this.isTableLoading = false;
          this.isLoading = false;
        });
    },
    handleVisibilityChange() {
      // Обновляем данные когда пользователь возвращается на вкладку
      // Только для Stripe/Paddle - там оплата происходит в новой вкладке
      if (document.visibilityState === 'visible' &&
          this.$store.getters.isLoggedIn &&
          (this.isStripePayment || this.isPaddlePayment || (this.isTinkoffPayment && this.hasTbankInvoices))) {
        const self = this;
        // Обновляем main_company (баланс)
        const mainCompanyId = this.$store.getters.getMainCompany?.id;
        if (mainCompanyId) {
          this.axios
            .get(`api/v1/main_companies/${mainCompanyId}`, {
              headers: {
                Authorization: this.$store.getters.getAuthToken,
              },
            })
            .then((response) => {
              this.$store.commit("setMainCompany", response.data);
            })
            .catch((error) => {
              console.log("Failed to refresh main_company:", error);
            });
        }
        // Обновляем транзакции (серверная пагинация — текущая страница)
        this.fetchTransactions(this.tableOptions);
      }
    },
    async openStripeInvoice(invoiceId) {
      if (!invoiceId || this.downloadingInvoiceId) return;
      
      this.downloadingInvoiceId = invoiceId;
      
      try {
        const response = await this.axios.get(
          `/api/v1/stripe_invoice_pdf/${invoiceId}`,
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        );
        
        if (response.data) {
          // Открываем hosted_invoice_url - там можно скачать и счёт, и квитанцию
          const url = response.data.hosted_invoice_url || response.data.invoice_pdf;
          if (url) {
            window.open(url, '_blank');
          } else {
            this.$notify(this.$t("errors.error"), this.$t("transfers.invoice_not_available"), "warning");
          }
        } else {
          this.$notify(this.$t("errors.error"), this.$t("transfers.invoice_not_available"), "warning");
        }
      } catch (error) {
        console.error('Failed to open invoice:', error);
        this.$notify(this.$t("errors.error"), this.$t("transfers.invoice_download_error"), "error");
      } finally {
        this.downloadingInvoiceId = null;
      }
    },
    async downloadPaddleInvoice(transactionId) {
      if (!transactionId || this.downloadingInvoiceId) return;
      
      this.downloadingInvoiceId = transactionId;
      
      try {
        const response = await this.axios.get(
          `/api/v1/paddle_invoice_pdf/${transactionId}`,
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        );
        
        if (response.data && response.data.url) {
          // Открываем PDF в новой вкладке
          window.open(response.data.url, '_blank');
        } else {
          this.$notify(this.$t("errors.error"), this.$t("transfers.invoice_not_available"), "warning");
        }
      } catch (error) {
        console.error('Failed to download invoice:', error);
        this.$notify(this.$t("errors.error"), this.$t("transfers.invoice_download_error"), "error");
      } finally {
        this.downloadingInvoiceId = null;
      }
    },
    async startInAppPurchase() {
      const self = this;
      
      try {
        this.isLoading = true;
        
        // Инициализация RevenueCat
        await purchaseService.initialize();
        
        // Получение продуктов
        const packages = await purchaseService.getProducts();
        
        if (!packages || packages.length === 0) {
          this.isLoading = false;
          this.$notify(this.$t("errors.error"), this.$t("errors.payment_creating"), "error");
          return;
        }
        
        const myProduct = packages[0].product;
        const priceInfo = purchaseService.getProductPrice(myProduct);
        
        this.in_app_purchase_currency = priceInfo.currency;
        this.in_app_purchase_price = priceInfo.price;
        
        // Конвертация валюты
        const url = `https://api.currencyapi.com/v3/latest?apikey=fca_live_sBpm46aqmsrZYxpWWytCPi2Uoy6OfCfxqrSshcpr&currencies=${self.currency}&base_currency=${self.in_app_purchase_currency}`;
        
        const response = await self.axios({ url, method: "get" });
        const value = response.data.data[self.currency].value;
        this.in_app_purchase_eur = (parseFloat(value * this.in_app_purchase_price)).toFixed(2);
        
        const without_commission = (this.in_app_purchase_eur - (parseFloat(this.in_app_purchase_eur * 0.3))).toFixed(2);
        
        this.isLoading = false;
        
        // Подготовка данных для диалога подтверждения
        this.purchaseConfirmData = {
          payment_sum: this.in_app_purchase_price,
          payment_currency: this.in_app_purchase_currency,
          top_up_sum: without_commission,
          top_up_currency: this.currency
        };
        
        // Показываем диалог подтверждения
        this.showPurchaseConfirmDialog = true;
        
      } catch (error) {
        this.isLoading = false;
        this.setError(error, this.$t("errors.error"));
      }
    },

    // Обертки для обратной совместимости
    startInAppPurchaseIos() {
      return this.startInAppPurchase();
    },
    startInAppPurchaseAndroid() {
      return this.startInAppPurchase();
    },
    
    // Подтверждение покупки из диалога
    async confirmPurchase() {
      this.showPurchaseConfirmDialog = false;
      this.isLoading = true;

      const mainCompanyId = this.$store.getters.getMainCompany?.id;
      if (!mainCompanyId) {
        // Без main_company_id покупка ушла бы анонимно и потерялась — не запускаем.
        this.isLoading = false;
        this.$notify(this.$t("errors.error"), this.$t("transfers.purchase_error_message"), "error");
        return;
      }

      // Атрибуты из актуального стора — выставятся перед покупкой (внутри
      // ensureIdentified), чтобы вебхук нёс $email/main_company_id сразу.
      const signedUser = this.$store.getters.getCurrentUser;
      const rcAttributes = {
        email: signedUser?.email,
        displayName: signedUser?.name || signedUser?.email,
        phoneNumber: signedUser?.phone || '',
        custom: {
          main_company_id: mainCompanyId.toString(),
          main_company_name: this.$store.getters.getMainCompany?.name || '',
          company_id: this.$store.getters.getCompany?.company?.id?.toString() || '',
          company_name: this.$store.getters.getCompany?.company?.name || '',
        },
      };

      try {
        // Выполнение покупки через RevenueCat (идентификация + атрибуты внутри)
        const purchaseResult = await purchaseService.purchaseProduct(mainCompanyId, rcAttributes);
        
        // Проверяем, была ли покупка отменена пользователем
        if (purchaseResult === null) {
          console.log('[IAP] Purchase cancelled by user');
          this.isLoading = false;
          return; // Выходим без показа уведомлений
        }
        
        console.log('[IAP] Purchase successful, scheduling verification fallback...');
        
        // Запланировать проверку через 10 минут (fallback на случай если webhook не придет).
        // Используем тот же mainCompanyId, под которым сделали покупку, — не перечитываем
        // стор (мог смениться за время нативного purchase sheet / смены филиала).
        try {
          await this.axios.post('/api/v1/revenuecat/schedule_verification', {
            main_company_id: mainCompanyId
          }, {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          });
          console.log('[IAP] Verification scheduled for 10 minutes');
        } catch (scheduleError) {
          console.error('[IAP] Failed to schedule verification:', scheduleError);
          // Не критично если не получилось - webhook должен сработать
        }
        
        // Покупка успешна - баланс будет обновлен автоматически через webhook
        this.$notify(this.$t("custom.success"), this.$t("transfers.purchase_success_message"), "success");
        
        // Обновляем список транзакций через 2 секунды
        setTimeout(() => {
          // Серверная пагинация: сброс на 1-ю страницу + рефетч (новая транзакция сверху).
          this.onTopupPaid();

          // Обновляем баланс компании
          this.axios
            .get(`api/v1/main_companies/${this.$store.getters.getCompany.company.main_company_id}`, {
              headers: {
                Authorization: this.$store.getters.getAuthToken,
              },
            })
            .then((response) => {
              this.$store.commit("setMainCompany", response.data);
            })
            .catch((error) => {
              console.error('Failed to refresh balance:', error);
            });
        }, 2000);
        
      } catch (error) {
        this.setError(error, this.$t("errors.error"));
      } finally {
        this.isLoading = false;
      }
    },
    
    // Отмена покупки из диалога
    cancelPurchase() {
      this.showPurchaseConfirmDialog = false;
      this.purchaseConfirmData = {
        payment_sum: 0,
        payment_currency: '',
        top_up_sum: 0,
        top_up_currency: ''
      };
    },
    subscribeToPaidTariff() {
      let self = this;
      this.$confirm({
        title: this.$t("custom.you_sure_title"),
        text: this.$t("custom.to_subscribe_to_paid_tariff_warning"),
        icon: "mdi-alert-circle-outline",
        confirmButtonText: this.$t("custom.yes"),
        cancelButtonText: this.$t("custom.no"),
      }).then((result) => {
        if (result && this.$store.getters.getMainCompany && this.$store.getters.getMainCompany.id) {
          this.isLoadingSubscribeToPaidTariff = true;
          this.axios
            .post(`/api/v1/subscribe_to_paid_v2`,
              {
                main_company_id: this.$store.getters.getMainCompany.id,
              },
              {
                headers: {
                  Authorization: this.$store.getters.getAuthToken,
                },
              })
            .then(() => {
              this.$notify(this.$t("custom.success"), this.$t("transfers.purchase_success_message"), "success");
              let main_company = this.$store.getters.getMainCompany;
              main_company.tariff = "Стандарт";
              this.$store.commit("setMainCompany", main_company);
            })
            .catch((error) =>
              this.setError(error, this.$t("errors.error"))
            )
            .finally(() => (this.isLoadingSubscribeToPaidTariff = false));
        }
      });
    },
    // FT-067: ссылка на юридический документ с учётом локали/домена
    rebillDocUrl(path) {
      const locale = this.$root.$i18n.locale;
      const tld = locale === "ru" ? "ru" : "com";
      return `https://rentprog.${tld}/${locale}/${path}`;
    },
    // FT-067: закрытие диалога автоплатежа со сбросом полей
    closeRebillDialog() {
      this.showRebillDialog = false;
      this.sum = 0;
      this.rebill_type = "Sum";
      this.rebill_sum = 1000;
      this.rebill_limit = 1000;
    },
    // FT-067: открыть диалог автоплатежа с префиллом сумм (правка — текущие настройки,
    // новая привязка — дефолт тарифа min_payment_sum*30).
    openRebillDialog() {
      // Тип автоплатежа всегда Sum (единственный) — селектор убран
      this.rebill_type = "Sum";
      if (this.hasActiveRebill) {
        this.rebill_sum = this.main_company.rebill_sum || this.defaultRebillSum;
        this.rebill_limit = this.main_company.rebill_limit || 1000;
      } else {
        this.rebill_sum = this.defaultRebillSum;
        this.rebill_limit = 1000;
      }
      this.showRebillDialog = true;
    },
    // FT-067: диспетчер кнопки автоплатежа — правка настроек без перепривязки при наличии
    // активного rebill_id; иначе нулевая (0 ₽) привязка новой карты через банк-форму.
    // Кнопка активна только при canSubmitRebill (согласие + валидные суммы по rules).
    submitRebill() {
      const mainCompany = this.$store.getters.getMainCompany;
      if (mainCompany && mainCompany.rebill_id) {
        this.updateRebillSettings();
      } else {
        this.addCardAttach();
      }
    },
    // FT-067: нулевая (0 ₽) привязка карты — AddCard → банк-форма (без списания, no-PCI).
    // Web: банк-форму открываем в НОВОЙ вкладке, а подтверждение делаем опросом confirm_add_card
    // в текущей — редирект-независимо (SuccessAddCardURL формы настраивается на терминале через
    // поддержку и может отсутствовать; на возврат браузера не закладываемся). Native (Capacitor)
    // и заблокированный popup → прежний редирект текущего webview (флаг для ResultSuccess).
    addCardAttach() {
      if (!this.requireStaff()) return;
      // Web: вкладку открываем синхронно в обработчике клика (иначе popup-блок после await).
      // Native (Capacitor): откроем in-app браузер Browser.open уже после ответа (блокировки нет).
      const isNativeApp = this.isNative;
      const bankWindow = isNativeApp ? null : window.open("", "_blank");
      this.isLoading = true;
      this.axios
        .post(
          "/api/v1/attach_rebill_card",
          {
            rebill_type: this.rebill_type,
            rebill_sum: this.rebill_sum,
            rebill_limit: this.rebill_limit,
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        )
        .then((response) => {
          if (response.data && response.data.PaymentURL) {
            const url = response.data.PaymentURL;
            if (isNativeApp) {
              // Capacitor: in-app браузер (SFSafariViewController/Custom Tabs); webview приложения
              // остаётся жив → опрашиваем confirm_add_card в фоне и сами закрываем браузер по confirmed.
              Browser.open({ url }).catch(() => {});
              this.pollCardBinding({ closeBrowser: true });
            } else if (bankWindow) {
              bankWindow.location.href = url;
              this.pollCardBinding();
            } else {
              // popup заблокирован (web) → редирект текущей вкладки (CON-04)
              localStorage.setItem("pending_card_binding", "1");
              window.location.replace(url);
              this.showRebillDialog = false;
            }
          } else {
            if (bankWindow) bankWindow.close();
            this.$notify(
              (response.data && response.data.error) || this.$t("errors.payment_creating"),
              (response.data && response.data.details) || "",
              "error"
            );
          }
        })
        .catch((error) => {
          if (bankWindow) bankWindow.close();
          this.isLoading = false;
          if (this.isTopupMinimumError(error)) this.refreshMainCompany();
          this.setError(error, this.$t("errors.payment_creating"));
        })
        .finally(() => (this.isLoading = false));
    },
    // FT-067: редирект-независимое подтверждение привязки — опрашиваем confirm_add_card, пока
    // Promoter не вернёт confirmed. Бэкенд идемпотентен: :pending пока привязка не завершена на
    // банк-форме, :confirmed после, :no_pending/:superseded — промоутнуто другим путём. По
    // истечении лимита тихо выходим — 15-мин CheckAddCardStateJob до-промоутит в любом случае.
    pollCardBinding(opts = {}) {
      this.cardBindingPolling = true;
      this.showRebillDialog = false;
      this.$notify(this.$t("transfers.card_binding_pending"), "", "info");
      let attempts = 0;
      const maxAttempts = 75; // ~5 минут при интервале 4с
      const stopBrowser = () => {
        // native: закрываем in-app браузер, возвращая пользователя в приложение
        if (opts.closeBrowser) Browser.close().catch(() => {});
      };
      const tick = () => {
        if (!this.cardBindingPolling) return;
        attempts += 1;
        this.axios
          .post(
            "/api/v1/confirm_add_card",
            {},
            { headers: { Authorization: this.$store.getters.getAuthToken } }
          )
          .then((response) => {
            const status = response.data && response.data.status;
            if (status === "confirmed") {
              this.cardBindingPolling = false;
              stopBrowser();
              this.$notify(this.$t("transfers.card_bound_success"), "", "success");
              this.refreshMainCompany();
            } else if (status === "no_pending" || status === "superseded") {
              this.cardBindingPolling = false;
              stopBrowser();
              this.refreshMainCompany();
            } else if (attempts < maxAttempts) {
              setTimeout(tick, 4000);
            } else {
              this.cardBindingPolling = false;
            }
          })
          .catch(() => {
            if (attempts < maxAttempts) {
              setTimeout(tick, 4000);
            } else {
              this.cardBindingPolling = false;
            }
          });
      };
      setTimeout(tick, 4000);
    },
    // FT-067: обновить main_company в сторе после привязки (маска карты + активный rebill в UI).
    // FT-150: бэкенд отбил сумму автоплатежа по пересчитанному полу (422 topup_below_minimum /
    // topup_invalid_sum) — payload на странице устарел, перечитываем main_company, чтобы
    // rebillFloor/правила/кнопка не держали отклонённую сумму валидной (финальное ревью 2026-09-17).
    isTopupMinimumError(error) {
      const response = error && error.response;
      const code = response && response.data && response.data.error_code;
      const codes = ["topup_below_minimum", "topup_invalid_sum", "rebill_sum_below_floor", "invalid_rebill_sum"];
      return !!response && response.status === 422 && codes.includes(code);
    },
    refreshMainCompany() {
      const mc = this.$store.getters.getMainCompany;
      if (!mc || !mc.id) return;
      this.axios
        .get(`/api/v1/main_companies/${mc.id}`, {
          headers: { Authorization: this.$store.getters.getAuthToken },
        })
        .then((response) => {
          this.$store.commit("setMainCompany", response.data);
        })
        .catch(() => {});
    },
    // FT-067: смена настроек автоплатежа без перепривязки карты (REQ-04).
    updateRebillSettings() {
      if (!this.requireStaff()) return;
      this.isLoading = true;
      this.axios
        .patch(
          "/api/v1/update_rebill_settings",
          {
            rebill_type: this.rebill_type,
            rebill_sum: this.rebill_sum,
            rebill_limit: this.rebill_limit,
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        )
        .then(() => {
          this.$notify(this.$t("transfers.rebill_updated"), "", "success");
          this.showRebillDialog = false;
        })
        .catch((error) => {
          this.isLoading = false;
          if (this.isTopupMinimumError(error)) this.refreshMainCompany();
          this.setError(error, this.$t("errors.payment_creating"));
        })
        .finally(() => (this.isLoading = false));
    },
    removeRecurrent() {
      if (this.isStaff) {
        this.isLoading = true;
        this.axios
          .post(
            "/api/v1/turn_off_rebill",
            {
              turn_off: true,
            },
            {
              headers: {
                Authorization: this.$store.getters.getAuthToken,
              },
            }
          )
          .then(() => {
            this.$notify(this.$t("transfers.removed_recurrent"), "", "success");
          })
          .catch((error) => {
            this.isLoading = false;
            this.setError(error, this.$t("errors.payment_creating"));
          })
          .finally(() => (this.isLoading = false));
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    // FT-071: отвязка EUR Stripe-карты — бэк делает PaymentMethod.detach + чистит PM/brand/last4.
    detachStripeAutopay() {
      if (!this.requireStaff()) return;
      this.stripeAutopayLoading = true;
      this.axios
        .post(
          "/api/v1/stripe_autopay_detach",
          {},
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        )
        .then(() => {
          this.$notify(this.$t("transfers.stripe_autopay_detached"), "", "success");
          this.refreshMainCompany();
        })
        .catch((error) => {
          this.setError(error, this.$t("errors.payment_creating"));
        })
        .finally(() => (this.stripeAutopayLoading = false));
    },
    deleteCard(CardId) {
      this.isLoading = true;
      this.axios
        .post(
          "/api/v1/delete_card",
          {
            CardId: CardId,
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        )
        .then((response) => {
          this.isLoading = false;
          let card = this.card_list.find((card) => card.CardId == CardId);
          card.Status = "D";
          this.card_list.splice(this.card_list.indexOf(card), 1, card);
        })
        .catch((error) => {
          this.isLoading = false;
          this.setError(error, this.$t("errors.card_delete"));
          console.log(error);
        })
        .finally(() => (this.isLoading = false));
    },
    // FT-073: per-plan месячные величины для карточек выбора тарифа (суточный тариф ×30, до 1 знака).
    isCurrentPlan(item) {
      return !!(this.main_company && this.main_company.plan && item.id === this.main_company.plan.standart_plan_id);
    },
    planUnitMonthly(item) {
      return roundMoney((Number(item.price) || 0) * monthlyMultiplier(item), item.currency);
    },
    planMinMonthly(item) {
      return roundMoney((Number(item.min_payment_sum) || 0) * monthlyMultiplier(item), item.currency);
    },
    planEstMonthly(item) {
      return mpMonthlyEstimate({ active_cars_count: this.main_company && this.main_company.active_cars_count, plan: item });
    },
    planPriceSuffix(item) {
      return item.payment_type === "fix_plan"
        ? this.$t("stripe_mp_lifecycle.per_month_short")
        : this.$t("stripe_mp_lifecycle.per_unit_month_short");
    },
    translateService(item) {
      // booking_management
      // calendar
      // fines_monitoring
      // docs_generation
      // analitics
      // day_plan
      // services
      // simple_cashbox
      // store
      // partners
      // agents
      // export_to_excel
      // pdf_docs_generation
      // email_notifications
      // sms_notifications
      // whatsapp_notifications
      // telegram_notifications
      // push_notifications
      // booking_website_form
      // localrent
      // yandex_prokat
      // amocrm
      // avito
      // payments_integration
      // beacons
      // api
      // webhooks
      // workers_schedule
      // history
      // yandex_cloud_recognize_document
      // integrations
      // multicurrency
      // multicashboxes
      if (item == "booking_management") {
        return this.$t("included_services.booking_management");
      } else if (item == "calendar") {
        return this.$t("included_services.calendar");
      } else if (item == "fines_monitoring") {
        return this.$t("included_services.fines_monitoring");
      } else if (item == "docs_generation") {
        return this.$t("included_services.docs_generation");
      } else if (item == "analitics") {
        return this.$t("included_services.analitics");
      } else if (item == "day_plan") {
        return this.$t("included_services.day_plan");
      } else if (item == "services") {
        return this.$t("included_services.services");
      } else if (item == "simple_cashbox") {
        return this.$t("included_services.simple_cashbox");
      } else if (item == "scheduler") {
        return this.$t("included_services.scheduler");
      } else if (item == "store") {
        return this.$t("included_services.store");
      } else if (item == "partners") {
        return this.$t("included_services.partners");
      } else if (item == "agents") {
        return this.$t("included_services.agents");
      } else if (item == "export_to_excel") {
        return this.$t("included_services.export_to_excel");
      } else if (item == "pdf_docs_generation") {
        return this.$t("included_services.pdf_docs_generation");
      } else if (item == "email_notifications") {
        return this.$t("included_services.email_notifications");
      } else if (item == "sms_notifications") {
        return this.$t("included_services.sms_notifications");
      } else if (item == "whatsapp_notifications") {
        return this.$t("included_services.whatsapp_notifications");
      } else if (item == "telegram_notifications") {
        return this.$t("included_services.telegram_notifications");
      } else if (item == "push_notifications") {
        return this.$t("included_services.push_notifications");
      } else if (item == "booking_website_form") {
        return this.$t("included_services.booking_website_form");
      } else if (item == "localrent") {
        return this.$t("included_services.localrent");
      } else if (item == "yandex_prokat") {
        return this.$t("yandex_prokat.yandex_prokat");
      } else if (item == "teletype") {
        return "TeleType";
      } else if (item == "amocrm") {
        return this.$t("included_services.amocrm");
      } else if (item == "avito") {
        return this.$t("included_services.avito");
      } else if (item == "payments_integration") {
        return this.$t("included_services.payments_integration");
      } else if (item == "beacons") {
        return this.$t("included_services.beacons");
      } else if (item == "api") {
        return this.$t("included_services.api");
      } else if (item == "webhooks") {
        return this.$t("included_services.webhooks");
      } else if (item == "workers_schedule") {
        return this.$t("included_services.workers_schedule");
      } else if (item == "history") {
        return this.$t("included_services.history");
      } else if (item == "yandex_cloud_recognize_document") {
        return this.$t("included_services.yandex_cloud_recognize_document");
      } else if (item == "opper_recognize_document") {
        return this.$t("included_services.opper_recognize_document");
      } else if (item == "integrations") {
        return this.$t("included_services.integrations");
      } else if (item == "multicurrency") {
        return this.$t("included_services.multicurrency");
      } else if (item == "multicashboxes") {
        return this.$t("included_services.multicashboxes");
      } else if (item == "check_client_reliability") {
        return this.$t("included_services.check_client_reliability");
      } else if (item == "okidoki") {
        return this.$t("included_services.okidoki");
      } else if (item == "docuseal") {
        return this.$t("included_services.docuseal");
      } else if (item == "messenger_max") {
        return this.$t("included_services.messenger_max");
      } else {
        return item;
      }
    },
    checkLimitsPlan(plan) {
      if (this.main_company && this.main_company.plan && (this.main_company.plan.standart_plan_id == plan.id || this.main_company.active_cars_count > plan.cars_count || this.main_company.active_users_count > plan.users_limit || this.main_company.companies_count > plan.branches_limit)) {
        return true;
      } else {
        return false;
      }
    },
    changePlan(plan_id) {
      // Гвард поднят из веток: смена тарифа компании — одна операция, а
      // проверок было две, в разных формах (денилист через == в MP-ветке,
      // отрицание в обычной). Агента пускали ОБЕ — расхождения между ними не
      // было, вопреки тому, что я написал в коммите 7f671136.
      if (!this.requireStaff()) return;
      // FT-073: MP-клиент / продуктовый триал → отдельный поток (превью проратации + Stripe), не legacy.
      if (this.isStripeMpClient || this.isMpPreSubscription) {
        this.mpChangePlan(plan_id);
        return;
      }
      this.$confirm({
          title: this.$t("plans.change_plan_warning_title"),
          text: this.$t("plans.change_plan_warning_text"),
          icon: "mdi-alert-circle-outline",
          confirmButtonText: this.$t("plans.confirm_change"),
          cancelButtonText: this.$t("custom.no"),
        }).then((result) => {
          if (result) {
            this.isLoadingChangePlan = true;
            this.axios
              .post(`/api/v1/change_plan`,
                {
                  plan_id: plan_id,
                },
                {
                  headers: {
                    Authorization: this.$store.getters.getAuthToken,
                  },
                })
              .then((response) => {
                let main_company = this.$store.getters.getMainCompany;
                main_company.plan = response.data.plan;
                this.$notify(this.$t("custom.success"), this.$t("transfers.purchase_success_message"), "success");
                this.$store.commit("setMainCompany", main_company);
                this.showChangePlanDialog = false;
              })
              .catch((error) => {
                // Инцидент 2026-08-06: понижение на тариф, где мест меньше, чем активных
                // сотрудников, теперь отбивается бэком — показываем, скольких надо снять.
                const data = (error && error.response && error.response.data) || {};
                if (data.error === "downgrade_blocked_users") {
                  this.$notify(this.$t("errors.error"), downgradeUsersMessage(data, this.$t.bind(this)), "warning");
                  return;
                }
                this.setError(error, this.$t("errors.error"));
              })
              .finally(() => (this.isLoadingChangePlan = false));
          }
        });
    },
    // FT-073: смена тарифа MP-клиента — превью (проратация/downgrade) → подтверждение → POST.
    mpChangePlan(plan_id) {
      this.isLoadingChangePlan = true;
      const wasPreSub = this.isMpPreSubscription; // FT-073: после триал-смены предложить подключить подписку
      const auth = { headers: { Authorization: this.$store.getters.getAuthToken } };
      this.axios
        .get(`/api/v1/stripe_mp_change_plan_preview`, { params: { standart_plan_id: plan_id }, ...auth })
        .then((response) => {
          const preview = response.data;
          if (preview.downgrade_blocked) {
            // Holistic-ревью P2: счётчики из ответа превью (authoritative), не из store (может быть stale).
            this.$notify(this.$t("errors.error"), downgradeMessage({ active_cars_count: preview.active_cars_count, cars_limit: preview.cars_limit }, this.$t.bind(this)), "warning");
            return;
          }
          // Места — второй ограничитель понижения (инцидент 2026-08-06).
          if (preview.downgrade_blocked_users) {
            this.$notify(this.$t("errors.error"), downgradeUsersMessage({ active_workers_count: preview.active_workers_count, users_limit: preview.users_limit }, this.$t.bind(this)), "warning");
            return;
          }
          return this.$confirm({
            title: this.$t("plans.change_plan"),
            text: changeConfirmText(preview, this.$t.bind(this)),
            icon: "mdi-swap-horizontal",
            showCancelButton: true,
            confirmButtonText: this.$t("plans.confirm_change"),
            cancelButtonText: this.$t("custom.no"),
          }).then((ok) => {
            if (!ok) return;
            return this.axios
              .post(`/api/v1/stripe_mp_change_plan`, { standart_plan_id: plan_id }, auth)
              .then(() => {
                this.$notify(this.$t("custom.success"), this.$t("transfers.purchase_success_message"), "success");
                this.showChangePlanDialog = false;
                this.refreshMainCompany();
                if (wasPreSub) this.promptConnectSubscription();
              });
          });
        })
        .catch((error) => this.mpHandleError(error))
        .finally(() => (this.isLoadingChangePlan = false));
    },
    // FT-073 (B): после триал-смены тарифа — явный nudge подключить подписку на выбранном тарифе.
    promptConnectSubscription() {
      this.$confirm({
        title: this.$t("stripe_mp_lifecycle.connect_prompt_title"),
        text: this.$t("stripe_mp_lifecycle.connect_prompt_text"),
        icon: "mdi-credit-card-plus-outline",
        showCancelButton: true,
        confirmButtonText: this.$t("stripe_mp_lifecycle.connect_subscription"),
        cancelButtonText: this.$t("stripe_mp_lifecycle.later")
      }).then((ok) => {
        if (ok) this.mpCheckout();
      });
    },
    // FT-073: отмена MP-подписки — превью неполного периода → подтверждение → POST cancel(invoice_now).
    mpCancel() {
      if (!this.requireStaff()) return;
      this.isLoadingMpCancel = true;
      const auth = { headers: { Authorization: this.$store.getters.getAuthToken } };
      this.axios
        .get(`/api/v1/stripe_mp_cancel_preview`, auth)
        .then((response) => {
          return this.$confirm({
            title: this.$t("stripe_mp_lifecycle.cancel_title"),
            text: cancelConfirmText(response.data, this.$t.bind(this)),
            icon: "mdi-cancel",
            iconColor: "error",
            showCancelButton: true,
            confirmButtonText: this.$t("stripe_mp_lifecycle.cancel_confirm_btn"),
            cancelButtonText: this.$t("custom.no"),
          }).then((ok) => {
            if (!ok) return;
            return this.axios
              .post(`/api/v1/stripe_mp_cancel`, {}, auth)
              .then(() => {
                this.$notify(this.$t("custom.success"), this.$t("stripe_mp_lifecycle.cancel_done"), "success");
                this.refreshMainCompany();
              });
          });
        })
        .catch((error) => this.mpHandleError(error))
        .finally(() => (this.isLoadingMpCancel = false));
    },
    // FT-073: код ошибки бэка → локализованное сообщение (downgrade с точными счётчиками).
    mpHandleError(error) {
      const data = (error && error.response && error.response?.data) || {};
      if (data.error === "downgrade_blocked") {
        this.$notify(this.$t("errors.error"), downgradeMessage({ active_cars_count: data.active_cars_count, cars_limit: data.cars_limit }, this.$t.bind(this)), "warning");
        return;
      }
      if (data.error === "downgrade_blocked_users") {
        this.$notify(this.$t("errors.error"), downgradeUsersMessage({ active_workers_count: data.active_workers_count, users_limit: data.users_limit }, this.$t.bind(this)), "warning");
        return;
      }
      if (data.error) {
        this.$notify(this.$t("errors.error"), this.$t(errorTextKey(data.error)), "warning");
        return;
      }
      this.setError(error, this.$t("errors.error"));
    },
    // FT-073: подключение MP-подписки (привязка карты) через хостед Stripe Checkout. window.open
    // синхронно (антипопап-блок) → редирект на session.url. При возврате — рефетч main_company
    // (подписка появится после вебхука checkout.session.completed). Зеркалит autopay-checkout (FT-071).
    mpCheckout() {
      if (!this.requireStaff()) return;
      this.isLoadingMpCheckout = true;
      const returnUrl = window.location.href;
      const win = this.isNative ? null : window.open("", "_blank");
      this.axios
        .post("/api/v1/stripe_mp_checkout", { success_url: returnUrl, cancel_url: returnUrl }, {
          headers: { Authorization: this.$store.getters.getAuthToken },
        })
        .then((response) => {
          const url = response.data && response.data.url;
          if (!url) {
            if (win) win.close();
            return;
          }
          if (this.isNative) {
            Browser.open({ url });
          } else if (win) {
            win.location.href = url;
          } else {
            window.location.href = url;
          }
          this.armMpReturnRefresh();
        })
        .catch((error) => {
          if (win) win.close();
          this.mpHandleError(error);
        })
        .finally(() => (this.isLoadingMpCheckout = false));
    },
    // Смена карты MP: Stripe Billing Portal (flow payment_method_update). Hosted-страница Stripe —
    // PCI/форма карты не у нас. Зеркалит mpCheckout (popup-окно + редирект + refresh при возврате).
    mpUpdateCard() {
      if (!this.requireStaff()) return;
      this.isLoadingMpUpdateCard = true;
      const returnUrl = window.location.href;
      const win = this.isNative ? null : window.open("", "_blank");
      this.axios
        .post("/api/v1/stripe_mp_update_card", { return_url: returnUrl }, {
          headers: { Authorization: this.$store.getters.getAuthToken },
        })
        .then((response) => {
          const url = response.data && response.data.url;
          if (!url) {
            if (win) win.close();
            return;
          }
          if (this.isNative) {
            Browser.open({ url });
          } else if (win) {
            win.location.href = url;
          } else {
            window.location.href = url;
          }
          this.armMpReturnRefresh();
        })
        .catch((error) => {
          if (win) win.close();
          this.mpHandleError(error);
        })
        .finally(() => (this.isLoadingMpUpdateCard = false));
    },
    // FT-073: при возврате фокуса после Checkout — обновить main_company (1 ретрай через 2.5с,
    // подписка пишется асинхронно вебхуком checkout.session.completed).
    armMpReturnRefresh() {
      const onFocus = () => {
        this.refreshMainCompany();
        setTimeout(() => this.refreshMainCompany(), 2500);
      };
      window.addEventListener("focus", onFocus, { once: true });
    },
    saveBillingAddress() {
      // Проверяем что все поля заполнены
      if (!this.billingAddress.country_code || !this.billingAddress.city || !this.billingAddress.first_line || !this.billingAddress.postal_code) {
        this.$notify(this.$t("errors.error"), this.$t("tariffs.billing_address_required"), "warning");
        return;
      }
      this.isLoading = true;
      this.axios
        .patch("/api/v1/company", {
          company: {
            billing_address: this.billingAddress,
          },
        },
        {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        )
        .then(() => {
          this.isLoading = false;
          this.$notify(this.$t("custom.success"), "", "success");
        })
        .catch((error) => {
          this.isLoading = false;
          this.setError(error, this.$t("errors.error"));
        })
        .finally(() => (this.isLoading = false));
    },
    returnState(state, paid, item = null) {
      // FT-147: у счетов T-Bank своя подпись и для оплаченных (EXECUTED/MANUAL) — ДО общей ветки «успех»
      const tbankKey = tbankLedgerStatusKey(item);
      if (tbankKey) return this.$t(tbankKey);
      if (state == 1 && paid) {
        return this.$t("other.success");
      } else if (state == 4) {
        return this.$t("other.returned");
      } else if (state == 3) {
        return this.$t("errors.error");
      } else if (item && item.payment_provider === 'stripe' && item.status === 'STRIPE_INVOICE_SENT') {
        return this.$t("transfers.awaiting_payment");
      } else if (item && item.payment_provider === 'paddle' && !paid) {
        return this.$t("transfers.awaiting_payment");
      } else if (state == 2) {
        return this.$t("transfers.expired");
      } else {
        return "?";
      }
    },
    // Цвет чипа статуса операции (леджер): успех/ошибка/возврат/ожидание/просрочен.
    stateChipColor(item) {
      if (item.state == 1 && item.paid) return "success";
      if (item.state == 3) return "error";
      if (item.state == 4) return "info";
      if (item.state == 2) return "warning";
      if (item && ((item.payment_provider === 'stripe' && item.status === 'STRIPE_INVOICE_SENT') ||
        (item.payment_provider === 'paddle' && !item.paid))) return "warning";
      // FT-147: цвет чипа счёта T-Bank
      const tbankColor = tbankLedgerChipColor(item);
      if (tbankColor) return tbankColor;
      return "grey";
    },
    formatCreatedAt(date) {
      return moment.parseZone(date).format("DD MMM H:mm:ss");
    },
    formatDate(date) {
      return moment(date).format("LL");
    },
    returnDate(item) {
      return moment.parseZone(item).format("LL");
    },
    checkAdmin() {
      return this.$store.getters.isSuperAdmin || this.$store.getters.isAdmin;
    },
    checkMobile() {
      if (this.$vuetify.breakpoint.name == "xs" || this.$vuetify.breakpoint.name == "sm") {
        this.isMobile = true;
        return true;
      } else {
        this.isMobile = false;
        return false;
      }
    },
  },
};
</script>

<style scoped>
/* FT-071: витрина EUR-автоплатежа — тинт работает на светлой и тёмной теме (low-alpha поверх фона) */
.rm-stripe-card {
  border: 1px solid rgba(67, 160, 71, 0.5);
  background: rgba(67, 160, 71, 0.07);
  border-radius: 12px;
}

/* FT-073: карточка-метрика использования (low-alpha фон работает на светлой и тёмной теме) */
.mp-metric {
  background: rgba(128, 128, 128, 0.1);
  border-radius: 10px;
}

.rotating {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* FT-067: компактный диалог автоплатежа — мелкий consent-лейбл, плоский expansion */
.rebill-consent >>> .v-label {
  font-size: 0.8125rem;
  line-height: 1.3;
}
.rebill-terms.v-expansion-panels >>> .v-expansion-panel::before {
  box-shadow: none;
}
.rebill-terms >>> .v-expansion-panel-content__wrap {
  padding-left: 0;
  padding-right: 0;
}

/* FT-077 редизайн «Кабинет счёта»: залитые скруглённые карточки (outlined невидим на тёмной теме) */
.tx-card {
  border-radius: 16px !important;
}
.tx-card.theme--dark {
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.tx-card.theme--light {
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.tx-icon-box {
  width: 46px;
  height: 46px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(25, 118, 210, 0.16);
}
.tx-row-ico {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  background: rgba(128, 128, 128, 0.14);
}
.tx-row + .tx-row {
  border-top: 1px solid rgba(128, 128, 128, 0.12);
}
.tx-amount {
  font-weight: 500;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.tx-perpage {
  max-width: 96px;
}
</style>
